import * as React from 'react';
import { action, computed } from 'mobx';
import { GiLift } from 'react-icons/gi';
import {
  FaCalendarCheck, FaCcAmazonPay, FaFileSignature, FaSignature,
} from 'react-icons/fa';
import { BsFileText, BsPersonLinesFill } from 'react-icons/bs';
import { AiFillDollarCircle } from 'react-icons/ai';
import { RefObject } from 'react';
import {
  AskType, Category, ConfirmType, GridLayoutHeader, PageProps,
  PageToolEvents, RetrieveFocusType, RowUpdate, TableLayoutHeader,
} from '../../../../constants';
import { EnrollmentTemplate } from './Enrollment.template';
import {
  EnrollmentAccountModel, EnrollmentActModel, EnrollmentChangePersonModel,
  EnrollmentCheckModel, EnrollmentContractInformationModel, EnrollmentContractLongModel,
  EnrollmentDivisionModel, EnrollmentHistoryModel, EnrollmentInchargeModel,
  EnrollmentReceivableModel, EnrollmentUnitModel, PersonPopupModel,
} from './models';
import { InfinityRetrieve, TabModel } from '../../../../models';
import { DragAndDropLayout, GridLayout, TableLayout } from '../../../../components';
import {
  Confirm, ConfirmDelete, ConfirmFail, ConfirmSuccess, ConfirmWarning,
} from '../../../../utils/confirm';
import {
  TabHeaderAccount, TabHeaderCheck, TabHeaderCheckDetail,
  TabHeaderHistory, TabHeaderIncharge, TabHeaderReceivable, TabHeaderUnit,
} from './tabs';
import { Date6Calculator, Today } from '../../../../utils/time';
import { PageComponent } from '../../../../utils';
import { HistoryPopupModel } from '../MonthContract/HistoryPopup.model';
import { RoutineModel } from '../../inspect/Routine/Routine.model';
import { Update } from '../../../../utils/array';
import { FileSelector } from '../../../../utils/file';

// ============================================================================
// 타입 정의
// ============================================================================

export interface EnrollmentActListItem {
  actcd: string;
  actnm: string;
  pernm: string;
}

export interface EnrollmentEqupListItem {
  equpcd: string;
  equpnm: string;
  contflag: string;
}

export interface BankItem {
  bankcd: string;
  banknm: string;
}

export interface staffListItem {
  divicd?: string;
  pernm?: string;
}

// ============================================================================
// 상수 정의
// ============================================================================

export enum EnrollmentTabId {
  UNIT,
  CONTRACKINFORMATION,
  HISTORY,
  INCHARGE,
  ACCOUNT,
  DIVISION,
  CHECK,
  RECEIVABLE,
}

export enum EnrollmentItemChangeTypes {
  bcontg,
  bstdate,
  bildyd,
  billgubun,
  addyn,
  evercd,
  evernight,
  contg,
  tax_combine,
  boryu,
}

export const EnrollmentItemChangeTypeNames = [
  'rnumflag', 'rnum', 'is_sex', 'entdate',
  'payiden', 'spjangcd', 'faminum', 'licence',
];

export enum BtnGubun {
  b_taxall,
  b_jiroall,
  b_remarkall,
  b_pic_ok,
  b_perid2all,
}

// 탭 관련 상수 매핑
const TAB_CONFIG = {
  titles: ['호기등록', '계약정보', '계약이력정보', '담당자이력정보', '거래처이력정보', '분할납부정보', '검사이력정보', '미수내역'],
  models: [
    EnrollmentUnitModel, EnrollmentContractInformationModel, EnrollmentHistoryModel,
    EnrollmentInchargeModel, EnrollmentAccountModel, EnrollmentDivisionModel,
    EnrollmentCheckModel, EnrollmentReceivableModel,
  ],
  functionNames: ['tab_1', 'tab_2', 'tab_3', 'tab_4', 'tab_5', 'tab_6', 'tab_7', 'tab_8'],
  dataStateNames: [
    'units', 'contractinformations', 'histories', 'incharges',
    'accounts', 'divisions', 'checks', 'receivables',
  ],
  updatesStateNames: [
    'unitUpdates', 'contractinformationUpdates', 'historyUpdates', 'inchargeUpdates',
    'accountUpdates', 'divisionUpdates', 'checkUpdates', 'receivableUpdates',
  ],
  focusedStateNames: [
    'unitsFocused', 'contractinformationsFocused', 'historiesFocused', 'inchargesFocused',
    'accountsFocused', 'divisionsFocused', 'checksFocused', 'receivablesFocused',
  ],
} as const;

// 레거시 호환용 export
export const EnrollmentTabTitles = TAB_CONFIG.titles;
export const EnrollmentTabModels = TAB_CONFIG.models;
export const EnrollmentTabFunctionNames = TAB_CONFIG.functionNames;
export const EnrollmentTabDataStateNames = TAB_CONFIG.dataStateNames;
export const EnrollmentTabUpdatesStateNames = TAB_CONFIG.updatesStateNames;
export const EnrollmentTabFocusedStateNames = TAB_CONFIG.focusedStateNames;

// ============================================================================
// 헬퍼 함수
// ============================================================================

/** 호기명에서 동/호 정보 추출 */
const extractDongHoFromEqupnm = (equpnm: string): { dong: string; ho: string } => {
  if (!equpnm?.includes('-')) return { dong: '', ho: '' };

  const hyphenCount = (equpnm.match(/-/g) || []).length;
  const firstIdx = equpnm.indexOf('-');

  if (hyphenCount === 1) {
    return {
      dong: equpnm.substring(0, firstIdx),
      ho: equpnm.substring(firstIdx + 1),
    };
  }

  const secondIdx = equpnm.indexOf('-', firstIdx + 1);
  return {
    dong: equpnm.substring(0, firstIdx),
    ho: equpnm.substring(secondIdx + 1),
  };
};

/** 청구주기 월 데이터를 객체로 변환 */
const parseDelmonFlags = (data: any): Record<string, boolean> => ({
  delmon1: data?.delmon1 === '01',
  delmon2: data?.delmon2 === '02',
  delmon3: data?.delmon3 === '03',
  delmon4: data?.delmon4 === '04',
  delmon5: data?.delmon5 === '05',
  delmon6: data?.delmon6 === '06',
  delmon7: data?.delmon7 === '07',
  delmon8: data?.delmon8 === '08',
  delmon9: data?.delmon9 === '09',
  delmon10: data?.delmon10 === '10',
  delmon11: data?.delmon11 === '11',
  delmon12: data?.delmon12 === '12',
});

/** 청구주기 플래그를 서버 전송용 값으로 변환 */
const serializeDelmonFlags = (state: any, prefix = 'delmon'): Record<string, string> => {
  const result: Record<string, string> = {};
  for (let i = 1; i <= 12; i += 1) {
    const key = `${prefix}${i}`;
    result[key] = state[key] ? i.toString().padStart(2, '0') : '00';
  }
  return result;
};

/** 초기 상태 생성 */
const createInitialState = (props: PageProps, params: any): EnrollmentState => {
  const { user } = props.publicStore;
  const isNotAdmin = user.kukcd !== '01' && params.menu !== 'w_tb_e601w';

  return {
    placeList: [],
    contractLongData: [],
    searchQuery: params?.searchQuery || '',
    pageSaveData: params?.pageSaveData || '',
    checkPageMove: !!params?.pageSaveData,
    perid: isNotAdmin ? user.perid : '',
    pernm: isNotAdmin ? user.pernm : '',
    actcd: '',
    actcd2: '',
    actcd3: '',
    wkactcd: '',
    wkactcd2: '',
    wkactcd3: '',
    wkactnm: '',
    wkactnm2: '',
    wkactnm3: '',
    contflag: '%',
    history: '%',
    bankcd: '',
    beperid: '',
    bepernm: '',
    changeGubun: '1',
    perid2: '',
    pernm2: '',
    data: [],
    focusedTab: undefined,
    basicAddressToggle: true,
    basicAddressToggle2: true,
    basicAddressToggle3: true,
    contractflag: '0',
    pushTotalCheck: false,
    contractVisible: false,
    divisionVisible: false,
    isVisibleAttachment: false,
    isVisibleAttachment2: false,
    tabContdate: '',
    remark: '',
    modalDate: Today.date(),
    // 분할납부 청구주기 초기값
    divideDelmonAll: true,
    ...Object.fromEntries(
      Array.from({ length: 12 }, (_, i) => [`divideDelmon${i + 1}`, true]),
    ),
    rekukcd: '',
    recontflag: '',
    changeUnitModal: false,
    currentUnitData: [],
    changeUnitData: [],
    testData: [],
    gpsURL: '',
    checkPersonDetailModal: false,
    taxDetailModal: false,
    personEmailDetailModal: false,
    checkDetail: [],
    checkDetailFocused: {} as EnrollmentCheckModel,
    actnm: '',
    mgubun: '',
    amt: '',
    addyn: '',
    contg: '',
    stdate: '',
    enddate: '',
    bcontg: '',
    bamt: '',
    baddyn: '',
    bstdate: '',
    benddate: '',
    bcontdate: '',
    chk: '',
    changePopUpFocused: {} as EnrollmentChangePersonModel,
    changePopUpData: [],
    PersonPopUpFocused: {} as PersonPopupModel,
    PersonPopUpData: [],
    taxweek: '',
    gubun: '',
  } as unknown as EnrollmentState;
};

// ============================================================================
// State 인터페이스
// ============================================================================

export interface EnrollmentState {
  // 탭별 데이터
  units?: Array<EnrollmentUnitModel>;
  unitUpdates?: Array<EnrollmentUnitModel>;
  unitFocused?: EnrollmentUnitModel;

  contractinformations?: Array<EnrollmentContractInformationModel>;
  contractinformationUpdates?: Array<EnrollmentContractInformationModel>;
  contractinformationsFocused?: EnrollmentContractInformationModel;
  contractinformationsFocusedNew?: any;

  histories?: Array<EnrollmentHistoryModel>;
  historyUpdates?: Array<EnrollmentHistoryModel>;
  histroiesFocused?: EnrollmentHistoryModel;

  incharges?: Array<EnrollmentInchargeModel>;
  inchargeUpdates?: Array<EnrollmentInchargeModel>;
  inchargesFocused?: EnrollmentInchargeModel;

  accounts?: Array<EnrollmentAccountModel>;
  accountUpdates?: Array<EnrollmentAccountModel>;
  accountsFocused?: EnrollmentAccountModel;

  divisions?: Array<EnrollmentDivisionModel>;
  divisionUpdates?: Array<EnrollmentDivisionModel>;
  divisionsFocused?: EnrollmentDivisionModel;

  checks?: Array<EnrollmentCheckModel>;
  checkUpdates?: Array<EnrollmentCheckModel>;
  checksFocused?: EnrollmentCheckModel;

  receivables?: Array<EnrollmentReceivableModel>;
  receivableUpdates?: Array<EnrollmentReceivableModel>;
  receivablesFocused?: EnrollmentReceivableModel;

  focusedTab?: TabModel;
  focuseddata?: any;
  focusedDetail?: any;

  // 드롭다운 데이터
  spjangcd?: string;
  bankcd?: string;
  rtclafi?: string;
  bildyds?: Array<any>;
  contbilds?: Array<any>;
  areanms?: Array<any>;
  evercds?: Array<any>;
  gubuns?: Array<any>;
  contgs?: Array<any>;
  contgubuns?: Array<any>;
  deljugis?: Array<any>;
  taxgubuns?: Array<any>;
  gubuns2?: Array<any>;
  kinds?: Array<any>;
  forms?: Array<any>;
  invers?: Array<any>;
  kcgubuns?: Array<any>;
  kcresults?: Array<any>;
  speeds?: Array<any>;
  evcds?: Array<any>;

  // UI 상태
  gpsURL: string;
  placeList?: Array<EnrollmentActModel>;
  img_pic?: string;
  img_sign?: string;
  percentImagePic?: number;
  percentImageSign?: number;
  basicAddressToggle?: boolean;
  basicAddressToggle2?: boolean;
  basicAddressToggle3?: boolean;
  fileDetailModal?: boolean;

  // 모달 상태
  unitRemoteModal?: boolean;
  selectModal?: boolean;
  onlyModal?: boolean;
  allModal?: boolean;
  stmon?: string;
  historyModal?: boolean;
  changeModal?: boolean;
  modalDate: string;
  checkPersonDetailModal: boolean;
  taxDetailModal: boolean;
  personEmailDetailModal: boolean;
  isDPModalVisible?: boolean;
  isPLCModalVisible?: boolean;
  isRecipeIntervalVisible?: boolean;
  isDivideIntervalVisible?: boolean;
  isInspectDetailModalVisible?: boolean;
  isVisibleAttachment: boolean;
  isVisibleAttachment2: boolean;

  // 청구주기 플래그
  delmonAll?: boolean;
  delmon1?: boolean; delmon2?: boolean; delmon3?: boolean; delmon4?: boolean;
  delmon5?: boolean; delmon6?: boolean; delmon7?: boolean; delmon8?: boolean;
  delmon9?: boolean; delmon10?: boolean; delmon11?: boolean; delmon12?: boolean;

  divideDelmonAll: boolean;
  divideDelmon1: boolean; divideDelmon2: boolean; divideDelmon3: boolean;
  divideDelmon4: boolean; divideDelmon5: boolean; divideDelmon6: boolean;
  divideDelmon7: boolean; divideDelmon8: boolean; divideDelmon9: boolean;
  divideDelmon10: boolean; divideDelmon11: boolean; divideDelmon12: boolean;

  // 기타 데이터
  pushTotalCheck: boolean;
  contractVisible?: boolean;
  divisionVisible?: boolean;
  popupData?: any;
  contractLongData: Array<EnrollmentContractLongModel>;
  contractData?: EnrollmentContractLongModel;
  faminum?: any;
  contractflag?: string;

  // 검색/필터
  searchQuery: string;
  contflag: string;
  perid: string;
  pernm: string;
  actcd: string;
  actcd2: string;
  actcd3: string;
  actnm2: string;
  actnm3: string;
  wkactcd: string;
  wkactcd2: string;
  wkactcd3: string;
  wkactnm: string;
  wkactnm2: string;
  taxweek: string;
  wkactnm3: string;
  gubun: string;
  beperid: string;
  bepernm: string;
  changeGubun: string;
  perid2: string;
  pernm2: string;
  history: string;

  // 현장/기본 정보
  acts?: Array<EnrollmentActListItem>;
  focusedAct?: EnrollmentActListItem;
  actInfo?: EnrollmentActModel;
  data?: EnrollmentActModel;

  // 팝업 데이터
  checkPersonFocused?: Array<any>;
  taxFocused?: Array<any>;
  personEmailFocused?: Array<any>;
  checkDetail: Array<EnrollmentCheckModel>;
  checkDetailFocused: EnrollmentCheckModel;
  historyFocusedData?: HistoryPopupModel;
  historyData?: Array<HistoryPopupModel>;
  remark?: string;

  // 계약연장 팝업
  actnm: string;
  mgubun: string;
  amt: string;
  addyn: string;
  contg: string;
  stdate: string;
  enddate: string;
  bcontg: string;
  bamt: string;
  baddyn: string;
  bstdate: string;
  benddate: string;
  bcontdate: string;
  chk: string;
  tabContdate: string;

  // 담당자변경 팝업
  changePopUpFocused: EnrollmentChangePersonModel;
  changePopUpData: Array<EnrollmentChangePersonModel>;
  PersonPopUpFocused: PersonPopupModel;
  PersonPopUpData: Array<PersonPopupModel>;

  // 기타
  checkPageMove?: boolean;
  saveData?: Array<EnrollmentUnitModel>;
  inspectDetailData?: Array<any>;
  inspectDetailItem?: RoutineModel;
  actinfoNew?: any;
  pageSaveData: any;
  rekukcd: string;
  recontflag: string;
  changeUnitModal: boolean;
  currentUnitData: Array<any>;
  changeUnitData: Array<any>;
  testData: Array<any>;
}

// ============================================================================
// 메인 컴포넌트
// ============================================================================

/**
 * 보수현장등록 컨트롤러
 * @window w_tb_e601_new
 * @category 보수현장등록
 */
export class Enrollment extends PageComponent<PageProps, EnrollmentState> implements PageToolEvents {
  // -------------------------------------------------------------------------
  // 멤버 변수
  // -------------------------------------------------------------------------

  tabs: Array<TabModel>;

  tabHeaders: Array<Array<TableLayoutHeader>>;

  tabHeaders2: Array<Array<GridLayoutHeader>>;

  // 테이블 참조
  table: React.RefObject<TableLayout> = React.createRef();

  tableUnit: React.RefObject<TableLayout> = React.createRef();

  /** 에이전트가 줄을 고르는 안쪽 표 — 호기를 골라야 승강기번호·정보센터연동이 그 호기에 한다. */
  get agentTables(): Record<string, React.RefObject<TableLayout>> {
    return { units: this.tableUnit };
  }

  tableCheck: React.RefObject<TableLayout> = React.createRef();

  tableCheckDetail: React.RefObject<GridLayout> = React.createRef();

  tableContractOnly: React.RefObject<TableLayout> = React.createRef();

  tableContractAll: React.RefObject<TableLayout> = React.createRef();

  tableHistory: React.RefObject<TableLayout> = React.createRef();

  tableChange: React.RefObject<TableLayout> = React.createRef();

  tableCheckPerson: React.RefObject<TableLayout> = React.createRef();

  tableChangeUnit: RefObject<DragAndDropLayout> = React.createRef();

  grid: React.RefObject<GridLayout> = React.createRef();

  gridReceivable: React.RefObject<GridLayout> = React.createRef();

  // 무한 스크롤 헬퍼
  infinity?: InfinityRetrieve;

  infinity2?: InfinityRetrieve;

  infinityModal?: InfinityRetrieve;

  infinityModal2?: InfinityRetrieve;

  infinityHistory?: InfinityRetrieve;

  // 업데이트 추적
  updatedRows?: Array<EnrollmentActModel>;

  updatedRows2?: Array<EnrollmentUnitModel>;

  updatedRows3?: Array<HistoryPopupModel>;

  updatedRows4?: Array<EnrollmentContractLongModel>;

  updatedRows5?: Array<EnrollmentChangePersonModel>;

  // 상태 플래그
  focusedUnitIndex: number = 0;

  isWholeGosiSync: boolean = false;

  isChangedElno: boolean = false;

  preMenu: string = '';

  focusedHistoryRow: number = 0;

  // -------------------------------------------------------------------------
  // 생성자
  // -------------------------------------------------------------------------

  constructor(props: PageProps, context: any) {
    super(props, context);
    this.props.onMount && this.props.onMount(this);

    const params = this.props.publicStore?.getPageParams();
    this.preMenu = params?.preMenu || '';

    this.tabs = this.createTabs();
    this.tabHeaders = this.createTabHeaders();
    this.tabHeaders2 = [TabHeaderCheckDetail, TabHeaderReceivable];

    const initialState = createInitialState(props, params);
    initialState.focusedTab = this.tabs[EnrollmentTabId.UNIT];
    this.state = props.state || initialState;
  }

  private createTabs(): TabModel[] {
    const icons = [GiLift, FaSignature, FaFileSignature, BsPersonLinesFill, BsFileText, FaCcAmazonPay, FaCalendarCheck, AiFillDollarCircle];
    return TAB_CONFIG.titles.map((title, i) => new TabModel(i.toString(), icons[i], title));
  }

  private createTabHeaders(): Array<Array<TableLayoutHeader>> {
    return [TabHeaderUnit, [], TabHeaderHistory, TabHeaderIncharge, TabHeaderAccount, [], TabHeaderCheck, []];
  }

  @computed
  get tabIndex(): EnrollmentTabId {
    return parseInt(this.state.focusedTab?.id || '0', 10);
  }

  // -------------------------------------------------------------------------
  // 라이프사이클
  // -------------------------------------------------------------------------

  @action
  async componentDidRecover() {
    const params = await this.props.publicStore?.getPageParams();
    this.preMenu = params?.preMenu || '';

    if (params?.searchQuery) {
      await this.SS({ searchQuery: params.searchQuery });
      await this.onRetrieveEvent();
    }

    if (params?.pageSaveData) {
      await this.SS({
        checkPageMove: true,
        pageSaveData: params.pageSaveData,
        actinfoNew: params.pageSaveData,
      });
      await this.onNewEvent();
    }
  }

  @action
  async onFirstOpenEvent() {
    await this.loadDropdownData();
    await this.onResisterMoveEvent();
  }

  private async loadDropdownData() {
    const { actionStore: api } = this.props;

    const dropdownConfigs = [
      { key: 'bildyds', func: 'wf_dd_e029' },
      { key: 'contbilds', func: 'wf_dd_ca510_028_01' },
      { key: 'areanms', func: 'wf_dd_e028_new' },
      { key: 'gubuns', func: 'wf_dd_da020_notjgubun' },
      { key: 'contgs', func: 'wf_dd_ca510_001_01' },
      { key: 'contgubuns', func: 'wf_dd_ca510_008_01' },
      { key: 'deljugis', func: 'wf_dd_ca510_007_01' },
      { key: 'taxgubuns', func: 'wf_dd_ca510_006_01' },
      { key: 'gubuns2', func: 'wf_dd_e002_01' },
      { key: 'kinds', func: 'wf_dd_e004_01' },
      { key: 'forms', func: 'wf_dd_e005_01' },
      { key: 'invers', func: 'wf_dd_e007_01' },
      { key: 'speeds', func: 'wf_dd_e008_01' },
      { key: 'kcgubuns', func: 'wf_dd_ca510_012_01' },
      { key: 'kcresults', func: 'wf_dd_ca510_018_01' },
      { key: 'evcds', func: 'wf_dd_e030_new' },
    ];

    // 병렬 로드
    dropdownConfigs.forEach(async ({ key, func }) => {
      const data = await api.dropdown(func);
      this.setState({ [key]: data?.items || [] } as any);
    });

    // 건물 드롭다운 (의존성 있음)
    const evercdsData = await api.dropdown('wf_dd_e101_aver', {
      contbild: this.state.actInfo?.contbild || '03',
    });
    this.setState({ evercds: evercdsData?.items || [] });
  }

  @action
  async onResisterMoveEvent() {
    if (this.state.checkPageMove) {
      await this.SS({ actinfoNew: this.state.pageSaveData });
      await this.onNewEvent();
    } else {
      await this.onRetrieveEvent();
    }
  }

  // -------------------------------------------------------------------------
  // CRUD 이벤트
  // -------------------------------------------------------------------------

  @action
  async onRetrieveEvent(type: RetrieveFocusType = RetrieveFocusType.DEFAULT) {
    const { actionStore: api } = this.props;
    this.preMenu = '';

    this.infinity = new InfinityRetrieve(
      {
        as_nm: this.state.searchQuery,
        wkactcd: this.state.wkactcd,
        gubun: this.state.contflag,
        perid: this.state.perid,
        history: this.state.history,
      },
      (params) => api.fxExec(`${TAB_CONFIG.functionNames[this.tabIndex]}_retrieve`, params),
      (items, next) => {
        this.setState({
          placeList: [...this.state.placeList || [], ...items.map((x) => new EnrollmentActModel(x))],
        }, next);
      },
      async () => {
        await this.SS({ placeList: [] });
        await this.infinity?.retrieveAll();
        this.grid.current?.setFocus(0);
      },
    );

    const last = this.state.actInfo;

    await this.resetStateForRetrieve();

    this.setState({}, async () => {
      this.table.current?.update();
      const index = await this.infinity?.retrieveTo(['actcd'], [last?.actcd], type, true) || 0;

      this.setState({
        rekukcd: this.infinity?.box?.kukcd,
        recontflag: this.infinity?.box?.contflag,
      });

      if (this.state.placeList && this.state.placeList.length > index) {
        this.grid.current?.setFocus(index);
      }
    });
  }

  private async resetStateForRetrieve() {
    this.setState({
      placeList: [],
      actInfo: new EnrollmentActModel(),
      data: new EnrollmentActModel([], false),
      [TAB_CONFIG.updatesStateNames[this.tabIndex]]: [],
      [TAB_CONFIG.focusedStateNames[this.tabIndex]]: undefined,
      [TAB_CONFIG.dataStateNames[this.tabIndex]]: [],
      unitFocused: new EnrollmentUnitModel(),
      contractinformationsFocused: new EnrollmentContractInformationModel(),
      contractinformationsFocusedNew: new EnrollmentContractInformationModel(),
      histroiesFocused: new EnrollmentHistoryModel(),
      inchargesFocused: new EnrollmentInchargeModel(),
      accountsFocused: new EnrollmentAccountModel(),
      divisionsFocused: new EnrollmentDivisionModel(),
      checksFocused: new EnrollmentCheckModel(),
      receivablesFocused: new EnrollmentReceivableModel(),
      focuseddata: [],
      focusedDetail: [],
      checkPageMove: false,
    } as any);
  }

  @action
  async onSaveEvent() {
    const { actionStore: api } = this.props;

    // 유효성 검사
    if (!this.validateBeforeSave()) return;

    await this.SS({ checkPageMove: false });

    let isSuccess: boolean;
    const tabId = this.tabIndex;

    switch (tabId) {
      case EnrollmentTabId.UNIT:
        isSuccess = await this.saveUnitTab(api);
        break;
      case EnrollmentTabId.CONTRACKINFORMATION:
        isSuccess = await this.saveContractTab(api);
        break;
      case EnrollmentTabId.DIVISION:
        isSuccess = await this.saveDivisionTab(api);
        break;
      default:
        isSuccess = await this.saveDefaultTab(api);
    }

    if (!isSuccess) return;

    await this.SS({
      unitFocused: new EnrollmentUnitModel(this.state.unitFocused),
      contractinformationsFocusedNew: undefined,
    });

    await this.onRetrieveEvent(RetrieveFocusType.DEFAULT);
    await this.tableUnit.current?.update();
    this.tableUnit.current?.setFocus(0, 1);
    await this.tableCheck.current?.update();
    this.tableCheck.current?.setFocus(0, 1);
  }

  private validateBeforeSave(): boolean {
    const dedate = this.state.contractinformationsFocused?.dedate;
    const delchk = this.state.contractinformationsFocused?.delchk;

    if ((dedate === '30' || dedate === '31') && delchk === '0') {
      ConfirmWarning.show('확인', '전자계산서발행 날짜가 말일로 입력되있습니다. 말일체크를 해주세요!');
      return false;
    }
    return true;
  }

  private async saveUnitTab(api: any): Promise<boolean> {
    const contynCheck = this.state.units?.filter((y) => y.contyn === '1');

    if (this.preMenu !== 'w_tb_xclient') {
      if (!ConfirmWarning.assert(
        contynCheck?.length.toString() === this.state.actInfo?.qty,
        '오류',
        '관리수와 호기대수가 맞지않습니다.',
      )) {
        return false;
      }
    }

    const params = {
      ...this.state.actInfo,
      items1: {
        ...this.state.unitFocused,
        contyn: this.state.unitUpdates?.[0]?.contyn || '1',
      },
    };

    return api.fxSave('tab_1_save', params, this.state.actInfo?.isNew);
  }

  private async saveContractTab(api: any): Promise<boolean> {
    if (this.state.contractinformationsFocusedNew === undefined && this.state.data?.isNew) {
      ConfirmWarning.show('경고', '신규 등록 시 호기등록 탭에서 저장 후 이용해주세요');
      return false;
    }

    const focused = this.state.contractinformationsFocused;
    const focusedNew = this.state.contractinformationsFocusedNew;

    if (focused?.mupernm === '' && focused?.muperid?.trim() !== '') {
      ConfirmWarning.show('경고', '무상계약진행자 이름 또는 사원번호가 누락되었습니다.\n 무상계약진행자 입력란으로 가서 더블클릭하여 선택 또는 엔터키를 눌러주십시오.');
      return false;
    }

    if (focused?.pernm === '' && focused?.perid?.trim() !== '') {
      ConfirmWarning.show('경고', '계약자 이름 또는 사원번호가 누락되었습니다.\n 계약자 입력란으로 가서 더블클릭하여 선택 또는 엔터키를 눌러주십시오.');
      return false;
    }

    const params = {
      ...this.state.actInfo,
      items2: {
        ...focused,
        perid: focused?.perid || focusedNew?.perid,
        pernm: focused?.pernm || focusedNew?.pernm,
        mgubun: focused?.mgubun || focusedNew?.mgubun,
        taxflag: focused?.taxflag || focusedNew?.taxflag,
        jirogubun: focused?.jirogubun || focusedNew?.jirogubun,
        contgubun: focused?.contgubun || focusedNew?.contgubun,
        contdate: focused?.contdate || focusedNew?.contdate,
        stdate: focused?.stdate || focusedNew?.stdate,
        enddate: focused?.enddate || focusedNew?.enddate,
        recedate: focused?.recedate || focusedNew?.recedate,
      },
    };

    return api.fxSave('tab_2_save', params, false);
  }

  private async saveDivisionTab(api: any): Promise<boolean> {
    const params = {
      ...this.state.actInfo,
      items6: this.state.divisionsFocused,
    };
    return api.fxSave('tab_6_save', params, false);
  }

  private async saveDefaultTab(api: any): Promise<boolean> {
    const i = this.tabIndex;
    return api.fxSave(
      `${TAB_CONFIG.functionNames[i]}_save`,
      {
        ...this.state.actInfo,
        items3: (this.state as any)[TAB_CONFIG.updatesStateNames[i]],
      },
      this.state.actInfo?.isNew,
    );
  }

  @action
  async onNewEvent() {
    const { actionStore: api } = this.props;

    if (!this.state.checkPageMove && this.hasSearchFilters()) {
      await this.clearSearchFilters();
      await this.onRetrieveEvent(RetrieveFocusType.FIRST);
    }

    if (this.state.data?.isNew) {
      ConfirmWarning.show('경고', '한번에 한 행만 추가하실 수 있습니다. 저장 후 다음 행을 등록해주세요.');
      return;
    }

    await this.infinity2?.retrieveAll();

    if (this.state.checkPageMove) {
      await this.createNewFromPageMove(api);
    } else {
      await this.createNew(api);
    }
  }

  private hasSearchFilters(): boolean {
    return this.state.searchQuery !== ''
      || this.state.contflag !== '%'
      || this.state.perid !== ''
      || this.state.pernm !== ''
      || this.state.wkactcd !== ''
      || this.state.wkactnm !== '';
  }

  private async clearSearchFilters() {
    await this.SS({
      searchQuery: '',
      contflag: '%',
      perid: '',
      pernm: '',
      wkactcd: '',
      wkactnm: '',
    });
  }

  private async createNewFromPageMove(api: any) {
    const data = await api.fxExec(
      `${TAB_CONFIG.functionNames[this.tabIndex]}_new`,
      {
        perid: this.props.publicStore?.user?.userid,
        spjangcd: this.props.publicStore?.user?.spjangcd,
      },
    );

    this.isChangedElno = false;

    await this.SS({
      placeList: [new EnrollmentActModel(this.state.actinfoNew, true), ...this.state.placeList || []],
      data: new EnrollmentActModel(this.state.actinfoNew, true),
      focuseddata: new EnrollmentActModel(this.state.actinfoNew, true),
      units: [],
      unitFocused: new EnrollmentUnitModel(),
      unitUpdates: [new EnrollmentUnitModel()],
      contractinformations: [],
      contractinformationUpdates: [],
      contractinformationsFocused: new EnrollmentContractInformationModel(),
      contractinformationsFocusedNew: undefined,
      incharges: [],
      inchargeUpdates: [],
      inchargesFocused: new EnrollmentInchargeModel(),
      accounts: [],
      accountsFocused: new EnrollmentAccountModel(),
      receivables: [],
      receivablesFocused: new EnrollmentReceivableModel(),
      histories: [],
      historyUpdates: [],
      histroiesFocused: new EnrollmentHistoryModel(),
      divisionsFocused: new EnrollmentDivisionModel(),
      checksFocused: new EnrollmentCheckModel(),
      contractVisible: false,
      divisionVisible: false,
      actInfo: new EnrollmentActModel({
        ...this.state.actinfoNew,
        actcd: data?.actcd,
        qty: '1',
        wkactcd: '001',
        wkactnm: '자사',
      }, true),
    });

    this.grid.current?.setFocus(0);
  }

  private async createNew(api: any) {
    const data = await api.fxExec(
      `${TAB_CONFIG.functionNames[this.tabIndex]}_new`,
      {
        perid: this.state.focuseddata?.perid,
        spjangcd: this.state.focuseddata?.spjangcd,
      },
    );

    if (!data) return;

    const isGreenP = this.props.publicStore.user.custcd === 'green_p';
    const newActData = isGreenP ? {
      ...data,
      actcd: '',
      actnm: '',
      actid: '',
      wkactcd: '',
      wkactnm: '',
    } : data;

    this.setState({
      placeList: [new EnrollmentActModel(newActData, true), ...this.state.placeList || []],
      actInfo: new EnrollmentActModel(newActData, true),
      data: new EnrollmentActModel(newActData, true),
      units: [],
      unitFocused: new EnrollmentUnitModel(),
      checkPageMove: false,
    });

    this.grid.current?.setFocus(0);
  }

  @action
  async onDeleteEvent() {
    const { actionStore: api } = this.props;
    const { actcd, actnm } = this.state.focuseddata || {};

    const text = `현장코드: ${actcd}, 현장명: ${actnm}`;
    if (await ConfirmDelete.show(text)) {
      if (await api.delete('현장을 삭제하시는게 맞습니까?', this.state.focuseddata)) {
        await this.onRetrieveEvent();
      }
    }
  }

  // -------------------------------------------------------------------------
  // 행 포커스 이벤트
  // -------------------------------------------------------------------------

  @action
  onRowFocusEvent(item: any) {
    const { actionStore: api } = this.props;
    this.updatedRows = [];
    this.setState({ contractVisible: false, divisionVisible: false });

    if (this.state.focuseddata?.isNew && this.state.checkPageMove) return;

    if (item?.equpcd) {
      this.onDetailRowFocusEvent(new EnrollmentUnitModel({
        custcd: item.custcd,
        spjangcd: item.spjangcd,
        actcd: item.actcd,
        equpcd: item.equpcd,
      }), 0);
    }

    this.isChangedElno = false;

    // 그린엘리베이터(평택) 특수 처리
    if (item?.isNew && this.props.publicStore.user.custcd === 'green_p') {
      this.setState({
        focuseddata: new EnrollmentActModel(item, item?.isNew),
        actInfo: new EnrollmentActModel(item, item?.isNew),
        contractinformations: [],
        incharges: [],
        accounts: [],
        receivables: [],
      });
      this.table.current?.update(true);
      return;
    }

    this.setState({ focuseddata: new EnrollmentActModel(item, item?.isNew) }, async () => {
      await this.loadRowFocusData(api, item);
    });
  }

  private async loadRowFocusData(api: any, item: any) {
    const data = await api.exec(
      Category.MAINTENANCE,
      `${TAB_CONFIG.functionNames[this.tabIndex]}_dw_1_RowFocuschanged`,
      { ...this.state.focuseddata, as_nm: this.state.searchQuery },
    );

    const i = this.tabIndex + 1;
    const itemsKey = `items${i}`;
    const itemsNumber = data?.[itemsKey];

    if (itemsNumber) {
      await this.setRowFocusDataWithItems(data, item, itemsNumber);
    } else {
      this.setState({ [TAB_CONFIG.dataStateNames[this.tabIndex]]: [] } as any, () => {
        this.table.current?.update();
      });
    }

    if (data?.items6) {
      this.setState({ divisionVisible: true });
    } else if (data) {
      await this.updateTablesAfterRowFocus(data, item, i);
    }

    if (i === 1 && !data?.items1) {
      this.setState({ units: [], unitFocused: new EnrollmentUnitModel() }, () => {
        this.tableUnit.current?.update();
      });
    } else if (i === 1) {
      await this.tableUnit.current?.update();
      this.state.units?.length && this.tableUnit.current?.setFocus(0);
    }
  }

  private async setRowFocusDataWithItems(data: any, item: any, itemsNumber: any[]) {
    const i = this.tabIndex;
    const ModelClass = TAB_CONFIG.models[i];

    this.setState({
      actInfo: item?.isNew ? this.state.actInfo : new EnrollmentActModel(data, item?.isNew),
      units: itemsNumber.map((x: any) => new EnrollmentUnitModel(x)),
      [TAB_CONFIG.dataStateNames[i]]: itemsNumber.map((x: any) => new (ModelClass as any)(x)),
      contractinformationsFocused: new EnrollmentContractInformationModel(
        data?.items2?.length ? data.items2[0] : {},
      ),
      divisionsFocused: new EnrollmentDivisionModel(
        data?.items6?.length ? data.items6[0] : {},
      ),
      checks: itemsNumber.map((x: any) => new EnrollmentCheckModel(x)),
      histroiesFocused: data?.items3?.length ? new EnrollmentHistoryModel(data.items3[0]) : undefined,
    } as any, () => this.table.current?.update());
  }

  private async updateTablesAfterRowFocus(data: any, item: any, i: number) {
    this.setState({
      actInfo: item?.isNew ? this.state.actInfo : new EnrollmentActModel(data, item?.isNew),
    } as any, async () => {
      await this.table.current?.update();
      await this.tableUnit.current?.update();
      await this.tableCheck.current?.update();

      if (this.state.units?.length) this.tableUnit.current?.setFocus(0);
      if (this.state.checks?.length) this.tableCheck.current?.setFocus(0);

      if (i === 8 && this.state.receivables?.length) {
        await this.SS({ [TAB_CONFIG.focusedStateNames[this.tabIndex]]: this.state.receivables[0] } as any);
      }

      if (this.state.histories?.length) this.tableHistory.current?.setFocus(0);

      // 빈 데이터 초기화
      ['items3', 'items4', 'items5', 'items8'].forEach((key) => {
        if (!data?.[key]) {
          const stateKey = {
            items3: 'contractinformations', items4: 'incharges', items5: 'accounts', items8: 'receivables',
          }[key];
          if (stateKey) this.setState({ [stateKey]: [] } as any);
        }
      });

      await this.table.current?.update();
    });
  }

  @action
  async onDetailRowFocusEvent(item: EnrollmentUnitModel, i: number) {
    const { actionStore: api } = this.props;

    if (!item) return;
    if (this.state.unitFocused?.isNew && this.tabIndex === EnrollmentTabId.UNIT) return;

    this.updatedRows = [];
    this.focusedUnitIndex = i;
    this.isChangedElno = false;

    if (this.state.data?.isNew) {
      await this.SS({ units: [], unitFocused: new EnrollmentUnitModel() });
      return;
    }

    await this.SS({ focusedDetail: item });

    const data = await api.exec(
      Category.MAINTENANCE,
      `${TAB_CONFIG.functionNames[this.tabIndex]}_dw_3_RowFocuschanged`,
      this.state.focusedDetail,
    );

    if (data) {
      await this.SS({ unitFocused: data });
    }
  }

  @action
  async onDetailCheckRowFocusEvent(item: any) {
    const { actionStore: api } = this.props;
    this.updatedRows = [];

    await this.SS({ focusedDetail: item });

    const data = await api.exec(
      Category.MAINTENANCE,
      `${TAB_CONFIG.functionNames[this.tabIndex]}_dw_10_RowFocuschanged`,
      this.state.focusedDetail,
    );

    if (data) {
      await this.SS({ checkDetail: data.items1, checksFocused: data });
    }

    if (this.state.checks?.length) {
      await this.tableCheckDetail.current?.forceRepaint(true);
      this.tableCheckDetail.current?.setFocus(0);
    }
  }

  @action
  async onCheckDetailRowFocusEvent(item: any) {
    this.setState({ checkDetailFocused: item });
  }

  // -------------------------------------------------------------------------
  // 탭 변경
  // -------------------------------------------------------------------------

  @action
  async onTabChange(focusedTab: TabModel) {
    const { user } = this.props.publicStore;

    // 권한 체크
    if (['1', '2'].includes(focusedTab.id)) {
      const hasPermission = this.checkTabPermission(user);
      if (!hasPermission) {
        ConfirmWarning.show('경고', '권한이 없습니다');
        return;
      }
    }

    // 미저장 데이터 확인
    const i = this.tabIndex;
    const updates = (this.state as any)[TAB_CONFIG.updatesStateNames[i]];

    if (this.state.focusedTab && updates?.length > 0) {
      const confirmed = await Confirm.show(
        '경고',
        '저장하지 않은 데이터가 있습니다.\n탭을 넘어가면 저장하지 않은 데이터는 사라집니다.\n넘어가시겠습니까?',
        ConfirmType.WARNING,
      );

      if (confirmed) {
        this.setState({
          [TAB_CONFIG.updatesStateNames[i]]: [],
          [TAB_CONFIG.focusedStateNames[i]]: undefined,
        } as any);
      } else {
        return;
      }
    }

    this.setState({
      focusedTab,
      [TAB_CONFIG.updatesStateNames[i]]: [],
      [TAB_CONFIG.focusedStateNames[i]]: undefined,
    } as any, () => this.onRowFocusEvent(this.state.focuseddata));
  }

  private checkTabPermission(user: any): boolean {
    if (this.state.recontflag === '0' && this.state.rekukcd !== '01') return false;
    if (user.contflag === '0' && user.kukcd !== '01') return false;
    return true;
  }

  // -------------------------------------------------------------------------
  // 모달 관리
  // -------------------------------------------------------------------------

  @action
  async checkModal(isOpen: boolean) {
    const { user } = this.props.publicStore;

    if (user.custcd === 'dmyong_g' && user.perid !== '00039' && user.divicd.substr(0, 2) !== 'AA') {
      ConfirmWarning.show('확인', '담당자변경 권한이 없습니다');
      return;
    }

    await this.SS({
      checkPersonDetailModal: isOpen,
      PersonPopUpFocused: new PersonPopupModel(),
    });
    await this.checkPersonRetrieve();

    if (!isOpen && !this.state.data?.isNew) {
      this.onRowFocusEvent(this.state.focuseddata);
    }
  }

  @action
  async taxModal(isOpen: boolean) {
    if (this.state.actInfo?.actnm === '' || this.state.actInfo?.wkactnm === '') {
      ConfirmWarning.show('경고', '현장명과 회사구분을 먼저 입력해주세요');
      return;
    }

    if (isOpen) {
      this.updatedRows2 = [];
      await this.taxRetrieve();
    }

    if (!isOpen && !this.updatedRows2?.length) {
      await this.SS({ taxDetailModal: isOpen });
      return;
    }

    if (!isOpen && this.updatedRows2?.length) {
      const shouldSave = await Confirm.ask(
        '경고',
        '수정된 내역이 있습니다. 저장 후 닫으시겠습니까?',
        '예', '아니오',
      );

      if (shouldSave === AskType.YES) {
        await this.onTaxSaveEvent();
        if (!this.state.data?.isNew) {
          this.onRowFocusEvent(this.state.focuseddata);
        }
      }
    }

    await this.SS({ taxDetailModal: isOpen });
  }

  @action
  async personEmailModal(isOpen: boolean) {
    await this.SS({ personEmailDetailModal: isOpen });
    await this.personEmailRetrieve();

    if (!isOpen && !this.state.data?.isNew) {
      this.onRowFocusEvent(this.state.focuseddata);
    }
  }

  @action
  async contractSelectModal(isOpen: boolean) {
    if (this.state.contractflag === '1') {
      this.setState({ contractflag: '0' });
    }
    this.setState({ selectModal: isOpen });
  }

  @action
  async contractOnlyModal(isOpen: boolean) {
    await this.contractRetrieve();
    this.setState({ onlyModal: isOpen });
  }

  @action
  async contractAllModal(isOpen: boolean) {
    await this.contractRetrieve2();
    this.setState({ allModal: isOpen });
  }

  @action
  async changePersonModal(isOpen: boolean) {
    this.setState({
      beperid: '', bepernm: '', perid2: '', pernm2: '',
    });
    await this.changeRetrieve();
    this.setState({ changeModal: isOpen });
  }

  @action
  async changePersonModalClose() {
    await this.changeRetrieve();
    this.setState({
      changeModal: false,
      beperid: '',
      bepernm: '',
      perid2: '',
      pernm2: '',
    });
  }

  @action
  async historyPopupModal(isOpen: boolean) {
    setTimeout(() => this.historyRetrieve(), 50);
    this.setState({ historyModal: isOpen });
  }

  // -------------------------------------------------------------------------
  // 청구주기 모달
  // -------------------------------------------------------------------------

  @action
  async openRecipeIntervalModal() {
    const { actionStore: api } = this.props;

    if (!this.state.contractinformationsFocused
      || this.state.contractinformationsFocused?.isNew
      || this.state.contractinformationsFocusedNew?.isNew) {
      ConfirmWarning.show('오류', '먼저 저장 후 사용해주세요');
      return;
    }

    await this.SS({ isRecipeIntervalVisible: true, delmonAll: false });

    const data = await api.retrieve({
      sub: 'w_popup_e101_pub',
      actcd: this.state.actInfo?.actcd,
      seq: this.state.contractinformationsFocused?.seq,
      deljugi: this.state.contractinformationsFocused?.deljugi,
    });

    const flags = parseDelmonFlags(data);
    await this.SS(flags as any);
    await this.SS({
      delmonAll: Object.values(flags).every(Boolean),
    });
  }

  @action
  async saveRecipeIntervalModal() {
    const { actionStore: api } = this.props;

    const saved = await api.save({
      sub: 'w_popup_e101_pub',
      actcd: this.state.actInfo?.actcd,
      seq: this.state.contractinformationsFocused?.seq,
      deljugi: this.state.contractinformationsFocused?.deljugi,
      ...serializeDelmonFlags(this.state),
    }, false);

    if (saved) {
      this.onRowFocusEvent(this.state.focuseddata);
    }

    await this.SS({ isRecipeIntervalVisible: false });
  }

  @action
  async closeRecipeIntervalModal() {
    await this.SS({ isRecipeIntervalVisible: false });
  }

  @action
  async onToggleRecipeDelmon(value: boolean) {
    const update: any = { delmonAll: value };
    for (let i = 1; i <= 12; i += 1) {
      update[`delmon${i}`] = value;
    }
    this.SS(update);
  }

  @action
  async openDivideIntervalModal() {
    const focused = this.state.divisionsFocused;
    await this.SS({
      isDivideIntervalVisible: true,
      divideDelmonAll: focused?.delflag === '1',
      ...Object.fromEntries(
        Array.from({ length: 12 }, (_, i) => [
          `divideDelmon${i + 1}`,
          // @ts-ignore
          focused?.[`delmon${i + 1}`] !== '00',
        ]),
      ),
    } as any);
  }

  @action
  async onToggleDivideDelmon(value: boolean) {
    const update: any = { divideDelmonAll: value };
    for (let i = 1; i <= 12; i += 1) {
      update[`divideDelmon${i}`] = value;
    }
    await this.SS(update);
  }

  @action
  async saveDivideIntervalModal() {
    await this.closeDivideIntervalModal();
  }

  @action
  async closeDivideIntervalModal() {
    const delmonData: any = { delflag: this.state.divideDelmonAll };
    for (let i = 1; i <= 12; i += 1) {
      delmonData[`delmon${i.toString().padStart(2, '0')}`] = (this.state as any)[`divideDelmon${i}`] ? i.toString().padStart(2, '0') : '00';
    }

    await this.SS({
      isDivideIntervalVisible: false,
      divisionsFocused: new EnrollmentDivisionModel({
        ...this.state.divisionsFocused,
        ...delmonData,
      }),
    });
  }

  // -------------------------------------------------------------------------
  // 데이터 조회 (팝업용)
  // -------------------------------------------------------------------------

  @action
  async checkPersonRetrieve() {
    const { actionStore: api } = this.props;

    const data = await api.fxExec('retrieve', {
      sub: 'w_tb_e601_perid',
      actcd: this.state.focuseddata?.actcd,
    });

    await this.SS({
      PersonPopUpData: data?.items?.map((x: any) => new PersonPopupModel(x)) || [],
    });

    await this.tableCheckPerson.current?.update(true);
    this.state.PersonPopUpData.length && this.tableCheckPerson.current?.setFocus(0, 1);
  }

  @action
  async taxRetrieve() {
    const { actionStore: api } = this.props;
    await this.SS({ PersonPopUpData: [] });

    const data = await api.fxExec('retrieve', {
      sub: 'w_tb_e601_cltcd',
      actcd: this.state.focuseddata?.actcd,
    });

    if (data?.items) {
      this.setState({ PersonPopUpData: data.items });
    }

    this.state.PersonPopUpData.length && this.tableCheckPerson.current?.setFocus(0);
    await this.tableCheckPerson.current?.update(true);
  }

  @action
  async personEmailRetrieve() {
    const { actionStore: api } = this.props;
    await this.SS({ PersonPopUpData: [] });

    const data = await api.fxExec('retrieve', {
      sub: 'w_tb_e601_actmail',
      actcd: this.state.focuseddata?.actcd,
    });

    if (data?.items) {
      this.setState({ PersonPopUpData: data.items }, async () => {
        await this.tableCheckPerson.current?.update(true);
        this.tableCheckPerson.current?.setFocus(0, 1);
      });
    }
  }

  @action
  async contractRetrieve() {
    await this.contractRetrieveCommon({
      stmon: this.state.contractinformationsFocused?.enddate.slice(0, 6),
      actcd: this.state.focuseddata?.actcd,
      wkactcd: this.state.focuseddata?.wkactcd,
    }, 'tableContractOnly');

    this.setState({
      stmon: this.state.contractinformationsFocused?.enddate.slice(0, 6),
      actcd2: this.state.focuseddata?.actcd,
      actnm2: this.state.focuseddata?.actnm,
      wkactcd2: this.state.focuseddata?.wkactcd,
      wkactnm2: this.state.focuseddata?.wkactnm,
    });
  }

  @action
  async contractRetrieve2() {
    await this.contractRetrieveCommon({
      stmon: Today.yearMon(),
      actcd: '',
      wkactcd: '',
    }, 'tableContractAll');

    this.setState({
      stmon: Today.yearMon(),
      actcd3: '',
      actnm3: '',
      wkactcd3: '',
      wkactnm3: '',
    });
  }

  private async contractRetrieveCommon(params: any, tableRef: 'tableContractOnly' | 'tableContractAll') {
    const { actionStore: api } = this.props;

    this.infinityModal = new InfinityRetrieve(
      { sub: 'w_popup_e101', ...params },
      (p) => api.fxExec('retrieve', p),
      (items) => {
        this.setState({ contractLongData: [...this.state.contractLongData, ...items] });
      },
      async () => {
        await this.SS({ contractLongData: [] });
        await this.infinityModal?.retrieveAll();
        await (this as any)[tableRef].current?.update(false);
        (this as any)[tableRef].current?.setFocus(0, 1);
      },
    );

    this.setState({ contractLongData: [] }, async () => {
      await this.infinityModal?.retrieveAll();
      await (this as any)[tableRef].current?.update(false);
      (this as any)[tableRef].current?.setFocus(0, 1);
    });
  }

  @action
  async contractRetrieveClicked() {
    await this.contractRetrieveCommon({
      stmon: this.state.contractinformationsFocused?.enddate.slice(0, 6),
      actcd: this.state.actcd2,
      wkactcd: this.state.wkactcd2,
    }, 'tableContractOnly');
  }

  @action
  async contractRetrieve2Clicked() {
    await this.contractRetrieveCommon({
      stmon: Today.yearMon(),
      actcd: this.state.actcd3,
      wkactcd: this.state.wkactcd3,
    }, 'tableContractAll');
  }

  @action
  async changeRetrieve() {
    const { actionStore: api } = this.props;

    this.infinityModal = new InfinityRetrieve(
      {
        sub: 'w_popup_e601',
        beperid: this.state.beperid,
        gubun: this.state.changeGubun,
      },
      (params) => api.fxExec('retrieve', params),
      (items) => {
        this.setState({ changePopUpData: [...this.state.changePopUpData, ...items] });
      },
      async () => {
        await this.SS({ changePopUpData: [] });
        await this.infinityModal?.retrieveAll();
        await this.tableChange.current?.update(false);
        this.tableChange.current?.setFocus(0, 1);
      },
    );

    this.setState({ changePopUpData: [] }, async () => {
      await this.infinityModal?.retrieveAll();
      await this.tableChange.current?.update(true);
      this.tableChange.current?.setFocus(0, 1);
    });
  }

  @action
  async historyRetrieve() {
    const { actionStore: api } = this.props;

    this.infinityHistory = new InfinityRetrieve(
      { sub: 'w_tb_e101_history', actcd: this.state.focuseddata?.actcd },
      (params) => api.fxExec('retrieve', params),
      (items) => this.setState({ historyData: [...items] }),
      async () => {
        await this.SS({ historyData: [] });
        await this.infinityHistory?.retrieveAll();
        await this.tableHistory.current?.update(true);
        this.tableHistory.current?.setFocus(0, 1);
      },
    );

    this.setState({ historyData: [] }, async () => {
      await this.infinityHistory?.retrieveAll();
      await this.tableHistory.current?.update(true);
      this.tableHistory.current?.setFocus(0, 1);
    });
  }

  // -------------------------------------------------------------------------
  // 저장/삭제 (팝업용)
  // -------------------------------------------------------------------------

  @action
  async onNewCheckPersonEvent() {
    const { actionStore: api } = this.props;
    const lastItem = this.state.PersonPopUpData[this.state.PersonPopUpData.length - 1];

    if (lastItem?.isNew) {
      ConfirmWarning.show('확인', '한번에 한 행만 추가할 수 있습니다.');
      return;
    }

    const data = await api.new({
      sub: 'w_tb_e601_perid',
      actcd: this.state.focuseddata?.actcd,
    });

    if (data) {
      if (data.be_enddate && this.state.PersonPopUpData.length > 0) {
        this.state.PersonPopUpData[this.state.PersonPopUpData.length - 1].enddate = data.be_enddate;
      }

      const newModel = new PersonPopupModel(data, true);
      this.setState({
        PersonPopUpData: [...this.state.PersonPopUpData, newModel],
        PersonPopUpFocused: newModel,
      }, () => this.tableCheckPerson.current?.update(true));
      this.tableCheckPerson.current?.setFocus(this.state.PersonPopUpData.length - 1, 1);
    }
  }

  @action
  async onNewTaxEvent() {
    const { actionStore: api } = this.props;

    const data = await api.new({
      sub: 'w_tb_e601_cltcd',
      actcd: this.state.focuseddata?.actcd,
    });

    if (data) {
      const lastSeq = this.state.PersonPopUpData.length > 0
        ? Number(this.state.PersonPopUpData[this.state.PersonPopUpData.length - 1].seq)
        : 0;
      data.seq = `0${lastSeq + 1}`;

      const newModel = new PersonPopupModel(data, true);
      this.setState({
        PersonPopUpData: [...this.state.PersonPopUpData, newModel],
        PersonPopUpFocused: newModel,
      }, () => this.tableCheckPerson.current?.update(true));
      this.tableCheckPerson.current?.setFocus(this.state.PersonPopUpData.length - 1, 1);
    }
  }

  @action
  async onNewPersonEmailEvent() {
    const { actionStore: api } = this.props;

    const data = await api.new({
      sub: 'w_tb_e601_actmail',
      actcd: this.state.focuseddata?.actcd,
    });

    if (data) {
      const newModel = new PersonPopupModel(data, true);
      this.setState({
        PersonPopUpData: [...this.state.PersonPopUpData, newModel],
        PersonPopUpFocused: newModel,
      }, () => this.tableCheckPerson.current?.update(true));
      this.tableCheckPerson.current?.setFocus(this.state.PersonPopUpData.length - 1, 1);
    }
  }

  @action
  async onCheckPersonSaveEvent() {
    const { actionStore: api } = this.props;
    const lastItem = this.state.PersonPopUpData[this.state.PersonPopUpData.length - 1];

    if (lastItem?.isNew && lastItem?.pernm === '') {
      ConfirmWarning.show('경고', '담당자명이 비어있습니다. \n담당자코드를 더블클릭하여 선택해주시거나 \n담당자코드를 입력 후 엔터를 눌러주시길 바랍니다.');
      return;
    }

    const data = await api.save({
      sub: 'w_tb_e601_perid',
      items: this.updatedRows2,
    }, lastItem?.isNew);

    if (data) {
      this.setState({ saveData: this.updatedRows2 }, async () => this.checkModal(false));
    }

    const changeData = {
      divinm: (this.state.saveData as any)?.[0]?.divinm || (this.state.saveData as any)?.[0]?.uv_arg3,
      perid: (this.state.saveData as any)?.[0]?.perid,
      pernm: (this.state.saveData as any)?.[0]?.pernm,
    };

    this.setState({
      actInfo: new EnrollmentActModel({
        ...this.state.actInfo,
        ...changeData,
      }, this.state.actInfo?.isNew),
    });
  }

  @action
  async onTaxSaveEvent() {
    const { actionStore: api } = this.props;

    const hasEmptyClient = this.state.PersonPopUpData?.some((x) => x.isNew && (!x.cltcd || !x.cltnm));
    if (hasEmptyClient) {
      ConfirmWarning.show('경고', '거래처명이 비어있습니다. \n거래처코드를 더블클릭하여 선택해주시거나 \n거래처코드를 입력 후 엔터를 눌러주시길 바랍니다.');
      return;
    }

    if (!this.updatedRows2?.length) {
      ConfirmWarning.show('경고', '변경된 내용이 없습니다.');
      return;
    }

    if (!this.state.PersonPopUpData?.length) {
      ConfirmWarning.show('경고', '최소 한개 이상의 거래처가 등록되어야 합니다.');
      return;
    }

    const currentCltInfo = this.state.PersonPopUpData[this.state.PersonPopUpData.length - 1];

    const data = await api.save({
      sub: 'w_tb_e601_cltcd',
      contflag: this.state.actInfo?.contflag,
      contdate: this.state.actInfo?.contdate,
      items: this.updatedRows2,
    }, this.state.actInfo?.new === '1');

    if (data) {
      await this.SS({ saveData: this.updatedRows2 });
    }

    await this.SS({
      actInfo: new EnrollmentActModel({
        ...this.state.actInfo,
        cltcd: currentCltInfo.cltcd,
        cltnm: currentCltInfo.cltnm,
      }, this.state.focuseddata?.isNew),
    });

    if (this.state.data?.isNew) return;

    await this.saveContractWithNewClient(api, currentCltInfo);
    await this.taxRetrieve();
    await this.onRetrieveEvent();
    this.updatedRows2 = [];
  }

  private async saveContractWithNewClient(api: any, currentCltInfo: any) {
    const focused = this.state.contractinformationsFocused;
    const focusedNew = this.state.contractinformationsFocusedNew;

    if (!focused || focused?.isNew || focusedNew?.isNew) return;

    const params = {
      ...this.state.actInfo,
      contdate: focused?.contdate || focusedNew?.contdate,
      cltcd: currentCltInfo.cltcd,
      cltnm: currentCltInfo.cltnm,
      items2: {
        ...focused,
        perid: focused?.perid || focusedNew?.perid,
        pernm: focused?.pernm || focusedNew?.pernm,
        mgubun: focused?.mgubun || focusedNew?.mgubun,
        taxflag: focused?.taxflag || focusedNew?.taxflag,
        jirogubun: focused?.jirogubun || focusedNew?.jirogubun,
        contgubun: focused?.contgubun || focusedNew?.contgubun,
        contdate: focused?.contdate || focusedNew?.contdate,
        stdate: focused?.stdate || focusedNew?.stdate,
        enddate: focused?.enddate || focusedNew?.enddate,
        recedate: focused?.recedate || focusedNew?.recedate,
        cltcd: currentCltInfo.cltcd,
        cltnm: currentCltInfo.cltnm,
      },
    };

    await api.fxExec('tab_2_save', params, this.state.focuseddata?.isNew);
  }

  @action
  async onPersonEmailSaveEvent() {
    const { actionStore: api } = this.props;

    const data = await api.save({
      sub: 'w_tb_e601_actmail',
      items: this.updatedRows2,
    }, true);

    if (data) {
      this.setState({ saveData: this.updatedRows2 }, () => this.checkPersonRetrieve());
    }

    this.setState({
      actInfo: new EnrollmentActModel({
        ...this.state.actInfo,
        actmail: (this.state.saveData as any)?.[0]?.actmail,
      }, this.state.actInfo?.isNew),
    });
  }

  @action
  async onCheckPersonDelete() {
    const { actionStore: api } = this.props;

    await api.delete('선택한 내역을 목록에서 삭제하시겠습니까?', {
      sub: 'w_tb_e601_perid',
      actcd: this.state.PersonPopUpFocused.actcd,
      seq: this.state.PersonPopUpFocused.seq,
    }) && await this.checkPersonRetrieve();

    await this.tableCheckPerson.current?.update(true);
    this.updatedRows2 = [];
  }

  @action
  async onTaxDelete() {
    const { actionStore: api } = this.props;

    if (this.state.PersonPopUpData.length < 2) {
      ConfirmWarning.show('경고', '마지막 거래처는 삭제가 불가능합니다.');
      return;
    }

    const deleted = await api.delete('선택한 내역을 목록에서 삭제하시겠습니까?', {
      sub: 'w_tb_e601_cltcd',
      actcd: this.state.PersonPopUpFocused.actcd,
      contflag: this.state.PersonPopUpFocused.contflag,
      contdate: this.state.PersonPopUpFocused.contdate,
      seq: this.state.PersonPopUpFocused.seq,
    });

    if (!deleted) return;

    await this.taxRetrieve();
    this.updatedRows2 = [];

    const currentCltInfo = this.state.PersonPopUpData[this.state.PersonPopUpData.length - 1];

    await this.SS({
      actInfo: new EnrollmentActModel({
        ...this.state.actInfo,
        cltcd: currentCltInfo.cltcd,
        cltnm: currentCltInfo.cltnm,
      }, this.state.focuseddata?.isNew),
    });

    if (this.state.data?.isNew) return;

    await this.saveContractWithNewClient(api, currentCltInfo);
    await this.taxRetrieve();
    await this.onRetrieveEvent();
  }

  @action
  async onPersonEmailDelete() {
    const { actionStore: api } = this.props;

    await api.delete('선택한 내역을 목록에서 삭제하시겠습니까?', {
      sub: 'w_tb_e601_actmail',
      actcd: this.state.PersonPopUpFocused.actcd,
      seq: this.state.PersonPopUpFocused.seq,
    }) && await this.checkPersonRetrieve();

    this.updatedRows2 = [];
  }

  @action
  async contractSave() {
    const { actionStore: api } = this.props;

    if (await api.save({
      new: '0',
      sub: 'w_popup_e101',
      items: this.state.contractLongData,
    }, true)) {
      this.tableContractOnly.current?.resetUpdates();
    }
    await this.contractOnlyModal(false);
    await this.onRetrieveEvent(RetrieveFocusType.DEFAULT);
  }

  @action
  async contractSave2() {
    const { actionStore: api } = this.props;

    if (await api.save({
      new: '0',
      sub: 'w_popup_e101',
      items: this.state.contractLongData,
    }, true)) {
      this.tableContractAll.current?.resetUpdates();
    }
    await this.contractAllModal(false);
    await this.onRetrieveEvent(RetrieveFocusType.DEFAULT);
  }

  @action
  async contractDelete() {
    const { actionStore: api } = this.props;

    if (!await Confirm.show('확인', '계약이력은 복구할수없습니다. 삭제하시겠습니까?', ConfirmType.QUESTION)) {
      return;
    }

    const focused = (this.state as any)[TAB_CONFIG.focusedStateNames[this.tabIndex]];
    if (await api.fxExec('wb_e101_history_delete', focused)) {
      await this.onRetrieveEvent(RetrieveFocusType.DEFAULT);
    }
  }

  @action
  async changeSave() {
    const { actionStore: api } = this.props;

    if (!this.state.beperid || !this.state.bepernm) {
      ConfirmWarning.show('경고', '전담당자명이 비어있습니다. \n담당자코드를 더블클릭하여 선택해주시거나 \n담당자명을 입력 후 엔터를 눌러주시길 바랍니다.');
      return;
    }

    if (!this.state.perid2 || !this.state.pernm2) {
      ConfirmWarning.show('경고', '후담당자명이 비어있습니다. \n담당자코드를 더블클릭하여 선택해주시거나 \n담당자명을 입력 후 엔터를 눌러주시길 바랍니다.');
      return;
    }

    if (await api.save({
      sub: 'w_popup_e601',
      beperid: this.state.beperid,
      perid: this.state.perid2,
      items: this.state.changePopUpData,
      stdate: this.state.modalDate,
    }, true)) {
      this.tableChange.current?.resetUpdates();
      this.setState({ changeModal: false });
    }
  }

  // -------------------------------------------------------------------------
  // 계약기록 관련
  // -------------------------------------------------------------------------

  @action
  async onNewHistoryEvent() {
    const { actionStore: api } = this.props;

    if (this.state.historyFocusedData?.isNew) {
      ConfirmWarning.show('경고', '한번에 한 행만 추가하실 수 있습니다.');
      return;
    }

    const data = await api.new({
      sub: 'w_tb_e101_history',
      actcd: this.state.focuseddata?.actcd,
    });

    if (data) {
      const newModel = new HistoryPopupModel(data, true);
      this.setState({
        historyData: [newModel, ...this.state.historyData || []],
        historyFocusedData: newModel,
      }, () => this.tableHistory.current?.update(true));
      this.tableHistory.current?.setFocus(0, 1);
    }
  }

  @action
  async onHistorySaveEvent() {
    const { actionStore: api } = this.props;

    if (this.state.remark?.trim() === '') {
      ConfirmWarning.show('경고', '계약내용이 없습니다. 계약내용을 입력해주세요');
      return;
    }

    if (!this.state.historyFocusedData) return;

    const data = await api.save({
      sub: 'w_tb_e101_history',
      items: this.state.historyData,
    }, this.state.historyFocusedData.new === '1');

    if (data) {
      await this.historyRetrieve();
    }
  }

  @action
  async historyDelete() {
    const { actionStore: api } = this.props;

    await api.delete('선택한 내역을 목록에서 삭제하시겠습니까?', {
      sub: 'w_tb_e101_history',
      actcd: this.state.focuseddata?.actcd,
      seq: this.state.historyFocusedData?.seq,
    }) && await this.historyRetrieve();

    this.updatedRows3 = [];
  }

  @action
  async onHistoryRowFocusEvent(item: HistoryPopupModel, index: number) {
    this.focusedHistoryRow = index;
    this.setState({ historyFocusedData: item, remark: item.remark });
  }

  @action
  async updateHistoryRemark(remark: string) {
    await Update.byIndex(this, 'historyData', this.focusedHistoryRow, 'remark', remark);
  }

  // -------------------------------------------------------------------------
  // 호기 관련
  // -------------------------------------------------------------------------

  @action
  async unitCreate() {
    const { actionStore: api } = this.props;

    if (this.state.units?.length) {
      if (!await Confirm.show('주의', '이미 저장된 호기가 있습니다! 삭제하고 다시생성하시겠습니까??', ConfirmType.QUESTION)) {
        return;
      }
    }

    if (this.state.actInfo?.qty === '0') {
      ConfirmWarning.show('경고', '관리대수를 1대 이상 입력해주세요');
      return;
    }

    const params = {
      ...this.state.actInfo,
      items1: {
        ...this.state.unitFocused,
        contyn: this.state.unitUpdates?.[0]?.contyn || '1',
      },
    };

    const isSuccess = await api.fxSave('tab_1_save', params, this.state.unitFocused?.new === '1');
    if (!isSuccess) return;

    const data = await api.fxExec('wb_e611_create_all', {
      actcd: this.state.actInfo?.actcd,
      qty: this.state.actInfo?.qty,
    });

    if (!data) return;

    await this.tableUnit.current?.update(false);
    await this.onRetrieveEvent(RetrieveFocusType.DEFAULT);
  }

  @action
  async unitAdd() {
    if ((this.state.units?.filter((x) => x.isNew).length || 0) > 0) {
      ConfirmWarning.show('오류', '한번에 한 호기만 등록이 가능합니다');
      return;
    }

    const { actionStore: api } = this.props;
    const data = await api.fxExec('wb_e611_new', {
      actcd: this.state.actInfo?.actcd,
      cltcd: this.state.actInfo?.cltcd,
    });

    if (data) {
      const newModel = new EnrollmentUnitModel(data, true);
      const newUnits = [...this.state.units || [], newModel];
      const contynCount = newUnits.filter((y) => y.contyn === '1').length;
      this.setState({
        units: newUnits,
        unitFocused: newModel,
        actInfo: new EnrollmentActModel({
          ...this.state.actInfo,
          qty: String(contynCount),
        }, this.state.actInfo?.isNew),
      }, async () => {
        await this.tableUnit.current?.update(true);
        this.state.units?.length && this.tableUnit.current?.setFocus(this.state.units.length - 1);
      });
    }
  }

  @action
  async unitDelete() {
    const { actionStore: api } = this.props;

    await api.fxDelete(
      'wb_e611_delete',
      `코드 : ${this.state.unitFocused?.equpcd} 호기명 : ${this.state.unitFocused?.equpnm}`,
      {
        actcd: this.state.unitFocused?.actcd,
        equpcd: this.state.unitFocused?.equpcd,
      },
    ) && await this.onRetrieveEvent();
  }

  // -------------------------------------------------------------------------
  // 정보센터 연동
  // -------------------------------------------------------------------------

  @action
  async openUnitRemoteControl() {
    const item = this.state.unitFocused;
    const { publicStore } = this.props;

    if (this.isChangedElno) {
      ConfirmWarning.show('오류', '승강기 번호 변경 후 우선 저장해주세요');
      return;
    }

    if (!item) {
      ConfirmWarning.show('오류', '호기를 먼저 선택해주세요');
      return;
    }

    if (item?.new === '1') {
      ConfirmWarning.show('오류', '한 번 저장 후 시도해주세요');
      return;
    }

    if (!item.elno || item.elno.length !== 7) {
      ConfirmWarning.show('오류', item.elno ? '승강기 번호가 이상합니다' : '승강기 번호를 입력 후 재시도 해주세요');
      return;
    }

    await publicStore.withManager(() => {
      if (!this.state.units?.length) {
        ConfirmWarning.show('오류', '호기를 먼저 선택해주세요');
      } else if (this.state.units.length > 1) {
        this.SS({ unitRemoteModal: true });
      } else {
        this.isWholeGosiSync = false;
        this.syncEqupFromGosi();
      }
    });
  }

  @action
  async closeRemoteControlModal(success: boolean, isMulti: boolean) {
    await this.SS({ unitRemoteModal: false });

    if (success) {
      this.isWholeGosiSync = isMulti;
      await this.syncEqupFromGosi();
    }
  }

  @action
  async syncEqupFromGosi() {
    const { publicStore, actionStore: api } = this.props;
    const item = this.state.unitFocused;

    // 유효성 검사
    if (!item || item?.new === '1' || !item.elno || item.elno.length !== 7) {
      const msg = !item ? '호기를 먼저 선택해주세요'
        : item?.new === '1' ? '한 번 저장 후 시도해주세요'
          : !item.elno ? '승강기 번호를 입력 후 재시도 해주세요'
            : '승강기 번호가 이상합니다';
      ConfirmWarning.show('오류', msg);
      return;
    }

    publicStore.doLoading();

    const defaultRawXml = await publicStore.runManagerScript('007', item.elno, true);

    if (defaultRawXml.indexOf('<status>404</status>') === -1) {
      const gizongRawJson = await publicStore.runManagerScript('008', item.elno);

      if (!gizongRawJson?.elvtrMgtNo1 || !gizongRawJson?.elvtrMgtNo2) {
        ConfirmWarning.show('오류', '건물관리번호가 없습니다\n잠시 후 재시도 부탁드립니다');
        publicStore.stopLoading();
        return;
      }

      const { elvtrMgtNo1, elvtrMgtNo2 } = gizongRawJson;
      const specRawJson = await publicStore.runManagerScript('005', `${elvtrMgtNo1},${elvtrMgtNo2}`);
      const insuranceRawJson = await publicStore.runManagerScript('004', `${elvtrMgtNo1},${elvtrMgtNo2}`);
      const beplanmonRawJson = await publicStore.runManagerScript('009', `${item.elno},${new Date6Calculator().add(-1).toString()}`);

      await api.fxExec('wb_e611_gosiload', {
        actcd: item.actcd,
        equpcd: item.equpcd,
        elno: item.elno,
        elvtrMgtNo1,
        elvtrMgtNo2,
        items1: defaultRawXml,
        items2: JSON.stringify({
          ds_list: specRawJson.ds_list.map((x: any) => ({
            COLUMN_NAME: x.COLUMN_NAME || '',
            SPEC_ITEM_NAME: x.SPEC_ITEM_NAME || '',
          })),
          ErrorMsg: specRawJson.ErrorMsg,
          ErrorCode: specRawJson.ErrorCode,
        }),
        items3: JSON.stringify({
          ds_list: insuranceRawJson.ds_list.map((x: any) => ({
            COMPANY_NM: x.COMPANY_NM || '',
            CONT_EN_DE: x.CONT_EN_DE || '',
            CONT_ST_DE: x.CONT_ST_DE || '',
            INSURANCE_KIND: x.INSURANCE_KIND || '',
            INSURANCE_STOCK_NO: x.INSURANCE_STOCK_NO || '',
          })),
          ErrorMsg: insuranceRawJson.ErrorMsg,
          ErrorCode: insuranceRawJson.ErrorCode,
        }),
        items4: JSON.stringify({
          chkcode: `${gizongRawJson.chkcode}`,
          chknum: `${gizongRawJson.chknum}`,
          elvtrMgtNo1: `${gizongRawJson.elvtrMgtNo1}`,
          elvtrMgtNo2: `${gizongRawJson.elvtrMgtNo2}`,
          status: `${gizongRawJson.status}`,
        }),
        items5: JSON.stringify(beplanmonRawJson.response),
      });
    }

    await this.onDetailRowFocusEvent(this.state.focusedDetail, this.focusedUnitIndex);
    publicStore.stopLoading();

    if (this.isWholeGosiSync) {
      if ((this.state.units?.length || 0) > this.focusedUnitIndex + 1) {
        this.focusedUnitIndex += 1;
        this.tableUnit.current?.setFocus(this.focusedUnitIndex);
        await this.waitNextEqup(item.elno);
        await this.syncEqupFromGosi();
      } else if (this.state.units?.length) {
        this.focusedUnitIndex = 0;
        this.tableUnit.current?.setFocus(this.focusedUnitIndex);
        await this.waitNextEqup(item.elno);
        await this.askSyncAdChk();
      }
    } else {
      await this.syncAdChk();
    }
  }

  async waitNextEqup(elno: string, resolve?: () => void): Promise<void> {
    return new Promise((res) => {
      if (this.state.unitFocused?.elno === elno) {
        setTimeout(() => this.waitNextEqup(elno, resolve || res), 500);
      } else if (resolve) {
        resolve();
      } else {
        res();
      }
    });
  }

  @action
  async askSyncAdChk() {
    const item = this.state.unitFocused;

    if (!item || item?.new === '1') {
      ConfirmWarning.show('오류', item ? '한 번 저장 후 시도해주세요' : '호기를 먼저 선택해주세요');
      return;
    }

    if (AskType.YES === await Confirm.ask(
      '검사 연동',
      '호기 연동은 완료되었습니다\n추가로 정보센터에서 검사 내역을 불러오시겠습니까?\n(검사 연동은 오래걸립니다)',
      '예', '아니오',
    )) {
      await this.syncAdChk();
    } else {
      await ConfirmSuccess.show('완료', '호기 연동이 완료되었습니다');
    }
  }

  @action
  async syncAdChk() {
    const { publicStore, actionStore: api } = this.props;

    let item = this.state.unitFocused;
    if (this.tabIndex === EnrollmentTabId.CHECK) {
      item = this.state.focusedDetail;
    }

    if (!item) {
      ConfirmWarning.show('오류', '호기를 먼저 선택해주세요');
      return;
    }

    if (!item?.elvtrmgtno1 || !item?.elvtrmgtno2) {
      ConfirmWarning.show('오류', '건물관리번호가 없습니다\n호기 연동 후 이용 부탁드립니다');
      return;
    }

    await publicStore.withManager(async () => {
      const adchkJson = await publicStore.runManagerScript('002', `${item?.elvtrmgtno1},${item?.elvtrmgtno2}`);

      const items = {
        ds_list: adchkJson.ds_list.map((x: any) => ({
          ADDRESS: `${x.ADDRESS || ''}`,
          APPLC_BE_DT: `${x.APPLC_BE_DT || ''}`,
          APPLC_EN_DT: `${x.APPLC_EN_DT || ''}`,
          BULD_NM: `${x.BULD_NM || ''}`,
          CNDL_RESULT_DIV: `${x.CNDL_RESULT_DIV || ''}`,
          CRFT_NO: `${x.CRFT_NO || ''}`,
          ELEVATOR_NO: `${x.ELEVATOR_NO || ''}`,
          ELVTR_ASIGN_NM: `${x.ELVTR_ASIGN_NM || ''}`,
          ELVTR_KIND_NM: `${x.ELVTR_KIND_NM || ''}`,
          ELVTR_MGT_NO1: `${x.ELVTR_MGT_NO1 || ''}`,
          ELVTR_MGT_NO2: `${x.ELVTR_MGT_NO2 || ''}`,
          INSPCT_DT: `${x.INSPCT_DT || ''}`,
          INSPCT_INSTT: `${x.INSPCT_INSTT || ''}`,
          INSPCT_INSTT_CD: `${x.INSPCT_INSTT_CD || ''}`,
          INSPCT_INSTT_CD_TY: `${x.INSPCT_INSTT_CD_TY || ''}`,
          INSPCT_KIND: `${x.INSPCT_KIND || ''}`,
          INSPCT_KIND_CD: `${x.INSPCT_KIND_CD || ''}`,
          INSPCT_NAME: `${x.INSPCT_NAME || ''}`,
          INSPCT_ODR: `${x.INSPCT_ODR || ''}`,
          INSPCT_RESULT_CD: `${x.INSPCT_RESULT_CD || ''}`,
          MAIN_INSPCT_NAME: `${x.MAIN_INSPCT_NAME || ''}`,
          PSEXAM_DIV: `${x.PSEXAM_DIV || ''}`,
          PSEXAM_YN: `${x.PSEXAM_YN || ''}`,
          RECPTN_DE: `${x.RECPTN_DE || ''}`,
          RECPTN_NO: `${x.RECPTN_NO || ''}`,
          items: [],
        })),
        ErrorMsg: adchkJson.ErrorMsg,
        ErrorCode: adchkJson.ErrorCode,
      };

      publicStore.doLoading();
      // eslint-disable-next-line no-restricted-syntax
      for (const x of items.ds_list) {
        if (x.INSPCT_RESULT_CD !== '10') {
          // eslint-disable-next-line no-await-in-loop
          const detailJson = await publicStore.runManagerScript(
            '003',
            `${x.INSPCT_INSTT_CD},${item?.elvtrmgtno1},${item?.elvtrmgtno2},${x.RECPTN_DE},${x.RECPTN_NO}`,
          );
          x.items = detailJson.ds_list;
        }
      }
      publicStore.stopLoading();

      await api.fxExec('wb_e073', {
        actcd: item?.actcd,
        equpcd: item?.equpcd,
        elno: item?.elno,
        elvtrMgtNo1: item?.elvtrmgtno1,
        elvtrMgtNo2: item?.elvtrmgtno2,
        items: JSON.stringify(items).replace(/\|/ig, '_'),
      });

      await this.onDetailCheckRowFocusEvent(this.state.focusedDetail);

      if (this.isWholeGosiSync) {
        if ((this.state.units?.length || 0) > this.focusedUnitIndex + 1 && item?.elno) {
          this.focusedUnitIndex += 1;
          this.tableUnit.current?.setFocus(this.focusedUnitIndex);
          await this.waitNextEqup(item.elno);
          await this.syncAdChk();
        } else {
          await ConfirmSuccess.show('완료', '호기, 검사 일괄 연동이 완료되었습니다');
        }
      } else {
        ConfirmSuccess.show('완료', '연동이 완료되었습니다');
      }
    });
  }

  @action
  async informationCenterSend() {
    ConfirmWarning.show('경고', '해당기능은 정보센터 고도화사업으로 인해 중단되었습니다. 정보센터에서 계약내용을 기입해주시기 바랍니다');
  }

  // -------------------------------------------------------------------------
  // 계약 관련
  // -------------------------------------------------------------------------

  @action
  async contractionResister() {
    const { actionStore: api, publicStore } = this.props;
    const { user } = publicStore;

    if (!this.state.actInfo?.custcd || this.state.focuseddata?.isNew) {
      ConfirmWarning.show('오류', '계약 등록시 호기등록탭 정보를 먼저 저장 후 이용해주세요');
      return;
    }

    if (this.state.contractinformationsFocused?.contdate) {
      ConfirmWarning.show('경고', '이미 계약을 등록했습니다!');
      return;
    }

    const data = await api.fxExec('wb_e101_new', {
      actcd: this.state.actInfo?.actcd,
      actnm: this.state.actInfo?.actnm,
      cltcd: this.state.actInfo?.cltcd,
      cltnm: this.state.unitFocused?.cltnm,
      contbild: '01',
      evercd: '01',
      dw_3cnt: (this.state.histories?.length || 0).toString(),
      state: this.state.actInfo?.state,
      enddate: this.state.actInfo?.enddate,
      perid: user.perid,
      pernm: user.pernm,
    });

    if (data) {
      await this.SS({
        contractVisible: true,
        contractinformationsFocusedNew: new EnrollmentContractInformationModel(data, true),
        contractinformationsFocused: new EnrollmentContractInformationModel(data, true),
      });

      // 설치현장에서 넘어온 경우 무상 계약내역 자동 셋팅
      if (!this.state.histories?.length && this.state.actInfo?.state === '2') {
        ConfirmWarning.show('확인', '설치현장에서 넘어온 현장입니다. 무상 계약내역이 자동 셋팅됩니다!');
        this.setState({
          contractinformationsFocused: new EnrollmentContractInformationModel({
            mgubun: '0',
            taxflag: '0',
            jirogubun: '0',
            contgubun: '03',
            seq: '001',
            deljugi: '01',
            delmon1: '01',
            delmon2: '02',
            delmon3: '03',
            delmon4: '04',
            delmon5: '05',
            delmon6: '06',
            delmon7: '07',
            delmon8: '08',
            delmon9: '09',
            delmon10: '10',
            delmon11: '11',
            delmon12: '12',
            contdate: data.recedate,
            ...data,
          }, false),
        });
      }
    }
  }

  @action
  async contractionDelete() {
    const { actionStore: api } = this.props;

    await api.fxDelete(
      'wb_e101_delete',
      `현장명 : ${this.state.actInfo?.actcd} 계약일자: ${this.state.contractinformationsFocused?.contdate}`,
      {
        actcd: this.state.actInfo?.actcd,
        seq: this.state.contractinformationsFocused?.seq,
      },
    ) && await this.onRetrieveEvent();
  }

  @action
  async divisionResister() {
    const { actionStore: api } = this.props;

    if (this.state.divisionsFocused?.recedate) {
      ConfirmWarning.show('경고', '이미 분할정보를 등록했습니다!');
      return;
    }

    const data = await api.fxExec('wb_e110_new', {
      actcd: this.state.actInfo?.actcd,
      cltcd: this.state.actInfo?.cltcd,
      cltnm: this.state.unitFocused?.cltnm,
      contbild: this.state.actInfo?.contbild,
      evercd: '',
      state: this.state.actInfo?.state,
      enddate: '',
      perid: (this.state.actInfo as any)?.perid || '',
      pernm: this.state.actInfo?.pernm,
    });

    if (data) {
      this.setState({
        divisionVisible: true,
        divisionsFocused: new EnrollmentDivisionModel(data, false),
      });
    }
  }

  @action
  async divisionDelete() {
    const { actionStore: api } = this.props;

    await api.fxDelete(
      'wb_e110_delete',
      `현장명 : ${this.state.actInfo?.actcd} 순번: 001`,
      { actcd: this.state.actInfo?.actcd, seq: '001' },
    ) && await this.onRetrieveEvent();
  }

  // -------------------------------------------------------------------------
  // ItemChanged 핸들러
  // -------------------------------------------------------------------------

  @action
  async billgubunChange(name: string, item: any) {
    const { actionStore: api } = this.props;

    if (name !== 'billgubun') return;

    const data = await api.fxExec('dw_4_itemchanged', {
      itemname: name,
      data: item.billgubun,
    });

    if (data) {
      this.setState({
        contractinformationsFocused: new EnrollmentContractInformationModel({
          ...this.state.contractinformationsFocused,
          ...data,
        }, this.state.contractinformationsFocused?.isNew),
      });
    }
  }

  @action
  async dw_3_itemChanged(value: boolean, item: any, rowUpdate: RowUpdate, itemname: string) {
    const { actionStore: api } = this.props;

    if (itemname !== 'contyn') return;

    const result = await Confirm.ask('확인', '계약내역을 변동하시겠습니까?\n계약체크는 변동시 바로 적용됩니다', '예', '아니오');
    if (result) return;

    rowUpdate({ ...item, contyn: value ? '1' : '0' });

    const data = await api.fxExec('dw_3_itemchanged', {
      itemname: 'contyn',
      data: value ? '1' : '0',
      actcd: item.actcd,
      equpcd: item.equpcd,
    });

    this.setState({
      actInfo: new EnrollmentActModel({ ...this.state.actInfo, qty: data.qty }),
    });
  }

  @action
  async itemChanged(type: number, item: any) {
    const { actionStore: api } = this.props;
    let data: any = [];

    const focusedDetailIndex = this.state.contractLongData.findIndex((x) => x.actnm === item.actnm);

    if (type === EnrollmentItemChangeTypes.bcontg) {
      data = await api.fxExec('dw_list_itemchanged', {
        sub: 'w_popup_e101',
        itemname: EnrollmentItemChangeTypes[type],
        addyn: item.addyn,
        amt: item.amt,
        enddate: item.enddate,
        data: item.bcontg,
      });
      if (data !== undefined) data.chk = '1';
    }

    if (type === EnrollmentItemChangeTypes.bildyd) {
      data = await api.fxExec('dw_2_itemchanged', {
        itemname: EnrollmentItemChangeTypes[type],
        data: item.bildyd,
      });
    }

    if (data) {
      this.setState({
        actInfo: new EnrollmentActModel({ ...this.state.actInfo, ...data }, this.state.data?.isNew),
      });
    }

    if (type === EnrollmentItemChangeTypes.bstdate) {
      data = await api.fxExec('dw_2_itemchanged', {
        itemname: EnrollmentItemChangeTypes[type],
        stdate: this.state.stdate,
        enddate: this.state.enddate,
      });
    }

    if (data) {
      this.setState({
        contractData: new EnrollmentContractLongModel({ ...this.state.contractData }),
        contractLongData: this.state.contractLongData.map((x, index) => (index === focusedDetailIndex
          ? new EnrollmentContractLongModel({ ...x, ...data, bcontg: item.bcontg }, x.isNew)
          : new EnrollmentContractLongModel(x, x.isNew))),
      }, () => {
        if (this.state.onlyModal) this.tableContractOnly.current?.update(false);
        else if (this.state.allModal) this.tableContractAll.current?.update(false);
      });

      this.setState({
        contractinformationsFocusedNew: new EnrollmentContractInformationModel({
          ...this.state.contractinformationsFocused,
          ...data,
        }, this.state.contractinformationsFocusedNew?.isNew),
        contractinformationsFocused: new EnrollmentContractInformationModel({
          ...this.state.contractinformationsFocused,
          ...data,
        }, this.state.contractinformationsFocused?.isNew),
      });
    }
  }

  @action
  async HistoryTabItemChanged(item: any, name: string, value: string, rowUpdate: RowUpdate) {
    const { actionStore: api } = this.props;

    const newItem = { ...item, [name]: value };
    rowUpdate(newItem);

    await api.fxExec('dw_8_itemchanged', {
      itemname: name,
      data: value,
      actcd: newItem.actcd,
      contdate: newItem.contdate,
      contg: newItem.contg,
      amt: newItem.amt,
      deljugi: newItem.deljugi,
      contgubun: newItem.contgubun,
      qty: newItem.qty,
    });
  }

  @action
  async itemChangedActcd(cd: string, cdnm: string) {
    const { actionStore: api } = this.props;

    const data = await api.fxExec('dw_2_itemchanged', {
      itemname: 'cltcd',
      data: cd,
      pubcltnm: cdnm,
      actcd: this.state.actInfo?.actcd,
    });

    await this.SS({
      actInfo: new EnrollmentActModel({
        ...this.state.actInfo,
        pubpernm: data?.pubpernm || '',
        publat: data?.anlat || '',
        publng: data?.anlng || '',
        pubzipcd: data?.pubzipcd || '',
        pubaddress: data?.pubaddress || '',
        oldpubaddress: data?.oldpubaddress || '',
        anzipcd: data?.pubzipcd || '',
        anaddress: data?.anaddress || '',
        anaddress2: data?.anaddress2 || '',
        oldanaddress: data?.oldanaddress || '',
        ancltnm: data?.ancltnm || '',
      }, this.state.focuseddata?.isNew),
    });

    if (AskType.YES === await Confirm.ask('확인', '승강기 소재지를 계산서발행처(거래처) 주소로 반영하시겠습니까?', '예', '아니요')) {
      await this.SS({
        actInfo: new EnrollmentActModel({
          ...this.state.actInfo,
          zipcode: data?.pubzipcd || '',
          address: data?.anaddress || '',
          oldaddress: data?.oldanaddress || '',
          address2: data?.anaddress2 || '',
        }, this.state.focuseddata?.isNew),
      });
    }
  }

  @action
  async dw_6_itemChanged(value: any, name: string) {
    const { actionStore: api, publicStore } = this.props;
    let data: any = {};

    const baseParams = {
      actcd: this.state.actInfo?.actcd,
      equpcd: this.state.unitFocused?.equpcd,
    };

    switch (name) {
      case 'remflag':
      case 'kcflag':
      case 'plc':
        data = await api.fxExec('dw_6_itemchanged', {
          itemname: name,
          data: value ? '1' : '0',
          ...baseParams,
        });
        break;
      case 'evcd_new':
        if (!this.state.unitFocused?.elno?.trim()) {
          ConfirmWarning.show('경고', '승강기 번호를 입력해주세요');
          return;
        }
        // eslint-disable-next-line no-case-declarations
        const beplanmonRawJson = await publicStore.runManagerScript(
          '009',
          `${this.state.unitFocused.elno},${new Date6Calculator().add(-1).toString()}`,
        );
        data = await api.fxExec('dw_6_itemchanged', {
          itemname: name,
          data: value ? '1' : '0',
          ...baseParams,
          elno: this.state.unitFocused.elno,
          items5: JSON.stringify(beplanmonRawJson.response),
        });
        break;

      case 'equpnm':
        // eslint-disable-next-line no-case-declarations
        const { dong, ho } = extractDongHoFromEqupnm(this.state.unitFocused?.equpnm || '');
        data = await api.fxExec('dw_6_itemchanged', {
          itemname: name,
          data: value,
          ...baseParams,
          ...(dong && ho ? { dong, ho } : {}),
        });
        break;

      case 'class2':
        data = await api.fxExec('dw_6_itemchanged', {
          itemname: name,
          data: value,
          ...baseParams,
          inver: this.state.unitFocused?.inver,
          speed: this.state.unitFocused?.speed,
          number: this.state.unitFocused?.number,
          nperson: this.state.unitFocused?.nperson,
        });
        break;

      case 'nperson':
      case 'number':
      case 'inver':
      case 'speed':
        data = await api.fxExec('dw_6_itemchanged', {
          itemname: name,
          data: value,
          ...baseParams,
          inver: this.state.unitFocused?.inver,
          speed: this.state.unitFocused?.speed,
          number: this.state.unitFocused?.number,
          nperson: this.state.unitFocused?.nperson,
          class2: this.state.unitFocused?.class2,
        });
        break;

      case 'incentive':
        data = await api.fxExec('dw_6_itemchanged', {
          itemname: name,
          data: value,
          ...baseParams,
        });
        break;
    }

    if (data) {
      this.setState({
        unitFocused: new EnrollmentUnitModel({
          ...this.state.unitFocused,
          ...data,
        }, this.state.unitFocused?.isNew),
      });
    }
  }

  changedElno() {
    this.isChangedElno = true;
  }

  // -------------------------------------------------------------------------
  // 버튼 클릭 핸들러
  // -------------------------------------------------------------------------

  @action
  async onButtonclick(datas: any, name: any) {
    const { actionStore: api } = this.props;
    const focused = this.state.contractinformationsFocused;

    const confirmMessages: Record<string, string> = {
      b_taxall: '전체 현장의 전자계산서발행 내용을 같은 내용으로 적용하시겠습니까?',
      b_jiroall: '전체 현장의 지로발행 내용을 같은 내용으로 적용하시겠습니까?',
      b_remarkall: '전체 현장의 적요를 같은 내용으로 적용하시겠습니까?',
    };

    const itemDataMap: Record<string, any> = {
      b_taxall: {
        taxflag: focused?.taxflag, taxchk: focused?.taxchk, dedate: focused?.dedate, delchk: focused?.delchk,
      },
      b_jiroall: {
        jirogubun: focused?.jirogubun, ungijun: focused?.ungijun, jirodate: focused?.jirodate, jirochk: focused?.jirochk,
      },
      b_remarkall: { remark: focused?.remark },
      b_pic_ok: {
        perid: focused?.perid, stdate: focused?.stdate, enddate: focused?.enddate, actcd: focused?.actcd, combine: focused?.combine,
      },
    };

    const btnName = BtnGubun[name];
    if (confirmMessages[btnName] && !await Confirm.show('주의', confirmMessages[btnName], ConfirmType.QUESTION)) {
      return;
    }

    const data = await api.fxExec('dw_1_buttonclicked', {
      new: focused?.isNew ? '1' : '0',
      cltcd: focused?.cltcd,
      actcd: this.state.actInfo?.actcd,
      itemname: btnName,
      data: datas,
      ...itemDataMap[btnName],
    });

    if (data) {
      this.setState({
        contractinformationsFocusedNew: new EnrollmentContractInformationModel({
          ...this.state.contractinformationsFocusedNew,
          ...data,
        }, this.state.contractinformationsFocusedNew?.isNew),
      });
    }
  }

  @action
  async onButtonclick2(datas: any, name: any) {
    const { actionStore: api } = this.props;

    if (BtnGubun[name] === 'b_perid2all') {
      if (!this.state.actInfo?.perid2 || !this.state.actInfo?.pernm2) {
        ConfirmWarning.show('경고', '보조자가 없습니다!');
        return;
      }

      const text = `현재${this.state.actInfo?.pernm} 담당자님의 현장으로 ${this.state.actInfo?.pernm2}님을 보조점검자로 일괄셋팅 하시겠습니까?`;
      if (!await Confirm.show('선택', text, ConfirmType.QUESTION)) {
        return;
      }
    }

    const data = await api.fxExec('dw_2_buttonclicked', {
      new: this.state.focuseddata?.isNew ? '1' : '0',
      cltcd: this.state.actInfo?.cltcd,
      actcd: this.state.actInfo?.actcd,
      itemname: BtnGubun[name],
      data: datas,
      perid: this.state.actInfo?.perid,
      perid2: this.state.actInfo?.perid2,
    });

    if (data) {
      this.setState({ actInfo: data }, () => this.onRetrieveEvent());
    }
  }

  // -------------------------------------------------------------------------
  // 체크 토글
  // -------------------------------------------------------------------------

  @action
  async updateCheckAllToggle(checked: boolean) {
    if (this.state.onlyModal || this.state.allModal) {
      this.setState({
        pushTotalCheck: checked,
        contractLongData: this.state.contractLongData?.map((x) => new EnrollmentContractLongModel({ ...x, chk: checked ? '1' : '0' }, x.isNew)),
      });
    } else if (this.state.changeModal) {
      this.setState({
        pushTotalCheck: checked,
        changePopUpData: this.state.changePopUpData?.map(
          (x) => new EnrollmentChangePersonModel(
            { ...x, chk: checked ? '1' : '0' },
            x.isNew,
          ),
        ),
      });
    }

    if (this.state.onlyModal) this.tableContractOnly.current?.update(false);
    else if (this.state.allModal) this.tableContractAll.current?.update(false);
    else if (this.state.changeModal) this.tableChange.current?.update(false);
  }

  // -------------------------------------------------------------------------
  // 기타 유틸리티
  // -------------------------------------------------------------------------

  @action
  async getGPS() {
    const { actionStore: api } = this.props;
    const data = await api.fxExec('wb_gps', {});
    this.setState({ gpsURL: data.url });
    window.open(this.state.gpsURL);
  }

  @action
  async photoModal(isOpen: boolean) {
    isOpen && ConfirmWarning.show('경고', '아직준비중입니다.');
  }

  @action
  async onAddressButton(value: any) {
    if (AskType.YES !== await Confirm.ask('확인', '현장명으로 주소를 검색하시겠습니까?', 'Yes', 'No')) {
      return;
    }

    const data = await this.props.modalStore.openSearchAddress(value, value);
    await this.SS({
      actInfo: new EnrollmentActModel({
        ...this.state.actInfo,
        zipcode: data.zipcode,
        address: data.roadAddress,
        address2: data.address,
        pubzipcd: data.zipcode,
        pubaddress: data.roadAddress,
        anzipcd: data.zipcode,
        anaddress: data.roadAddress,
        anaddress2: data.address,
        publat: data.y,
        publng: data.x,
      }, this.state.focuseddata?.isNew),
    });
  }

  @action
  async onLinkEvent() {
    const { actionStore: api } = this.props;

    await api.fxExec('wb_e073', {
      elno: this.state.checksFocused?.elno,
      elvtrmgtno1: this.state.checksFocused?.elvtrmgtno1,
      elvtrmgtno2: this.state.checksFocused?.elvtrmgtno2,
      actcd: this.state.checksFocused?.actcd,
      equpcd: this.state.checksFocused?.equpcd,
    });
  }

  @action
  goChkPreset() {
    this.props.publicStore.go('/w_tb_e611_035_03', {
      searchQuery: this.state.actInfo?.actnm,
    });
  }

  async openTelnum() {
    if (!this.state.actInfo?.tel) {
      ConfirmWarning.show('오류', '전화번호가 없습니다.');
      return;
    }
    this.props.publicStore.go('/w_tb_e911', { searchQuery: this.state.actInfo?.actnm });
  }

  @action
  onRowFocusEvent2(item: PersonPopupModel) {
    this.setState({
      PersonPopUpFocused: new PersonPopupModel(item, this.state.PersonPopUpFocused?.isNew),
    });
  }

  // -------------------------------------------------------------------------
  // 도면/PLC/검사계획 모달
  // -------------------------------------------------------------------------

  @action
  async copyDP() {
    if (!this.state.unitFocused?.dpattcnt || parseInt(this.state.unitFocused?.dpattcnt, 10) < 1) {
      ConfirmWarning.show('오류', '선택한 호기가 도면이 없습니다');
      return;
    }

    const { actionStore: api } = this.props;
    await api.fxExec('wb_dpcopy', {
      actcd: this.state.actInfo?.actcd,
      equpcd: this.state.unitFocused?.equpcd,
    });

    this.onRowFocusEvent(this.state.focuseddata);
  }

  @action
  async copyCCTV() {
    const { actionStore: api } = this.props;
    const result = await Confirm.ask(
      '선택',
      `현재 선택된 호기 ${this.state.unitFocused?.equpcd}의 셋팅으로 다른 호기들도 전부 셋팅(반영) 하시겠습니까?`,
      '예', '아니오',
    );

    if (result) return;

    await api.fxExec('wb_cctv', {
      actcd: this.state.actInfo?.actcd,
      cctv: this.state.unitFocused?.cctv,
    });

    this.onRowFocusEvent(this.state.focuseddata);
  }

  @action
  openPLC() {
    ConfirmWarning.show('...', '현재 이 기능은 개발중입니다');
  }

  @action
  async closeDPModal() {
    await this.SS({ isDPModalVisible: false });
    this.onRowFocusEvent(this.state.focuseddata);
  }

  @action
  async closePLCModal() {
    await this.SS({ isPLCModalVisible: false });
    this.onRowFocusEvent(this.state.focuseddata);
  }

  @action
  async openInspectDetailModal(item: RoutineModel) {
    const { actionStore: api } = this.props;
    const data = await api.retrieve({ sub: 'w_popup_e073_fail', ...item });

    if (data?.items) {
      this.setState({
        isInspectDetailModalVisible: true,
        inspectDetailData: data.items,
        inspectDetailItem: item,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 호기 코드변경
  // -------------------------------------------------------------------------

  async openChangeUnit() {
    await this.SS({ changeUnitModal: true, currentUnitData: [], changeUnitData: [] });
    await this.onRetrieveChangeUnit();
  }

  closeChangeUnit() {
    this.SS({ changeUnitModal: false, currentUnitData: [], changeUnitData: [] });
  }

  async onRetrieveChangeUnit() {
    const { actionStore: api } = this.props;

    const data = await api.fxExec('retrieve', {
      sub: 'w_popup_e611_codechange',
      actcd: this.state.focuseddata.actcd,
    });

    await this.SS({ currentUnitData: data?.items, changeUnitData: data?.items });
    this.tableChangeUnit.current?.update(true);
  }

  async onSaveChangeUnit() {
    const { actionStore: api } = this.props;

    const result = await Confirm.ask(
      '확인',
      '코드변경은 진행도중에 절대 강제종료하시면 안됩니다. 완료될때까지 기달려주세요 진행하시겠습니까?',
      '예', '아니오',
    );

    if (result) return;

    const changeItems = this.state.changeUnitData.map((x) => ({
      equpcd: x.equpcd,
      equpcd_old: x.equpcd_old,
      addrow: x.equpcd === x.equpcd_old ? '0' : '1',
    }));

    await api.save({
      sub: 'w_popup_e611_codechange',
      actcd: this.state.focuseddata.actcd,
      items: changeItems,
    }, false) && await this.onRetrieveEvent();
  }

  // -------------------------------------------------------------------------
  // 엑셀 호기 일괄등록
  // -------------------------------------------------------------------------

  async unitOnExcelEvent() {
    if (this.state.units!.length > 0) {
      const warning = await Confirm.ask(
        '경고',
        '이미 저장된호기는 삭제되고 엑셀에 있는 정보로 덮어쓰기 됩니다\n\n 진행하시겠습니까?',
        '예', '아니오',
      );
      if (warning) return;
    }

    const result = await Confirm.ask(
      '선택',
      '승강기정보센터에 있는 호기내용[EXCEL]으로\n 일괄등록 합니다.\n *정보센터파일위치: 업체관리 -> 유지관리현장 -> 엑셀로보내기\n\n 정보센터에서 받은 해당 파일[EXCEL]을 선택해주세요\n 진행하시겠습니까?',
      '예', '아니오',
    );

    if (!result) {
      try {
        const files = await FileSelector.single(false);
        await this.unitExcelUpload(files[0].name, files[0]);
      } catch {
        ConfirmFail.show('오류', '엑셀 처리중 알 수 없는 문제가 발생하였습니다.');
      }
    }
  }

  async unitExcelUpload(filename: string, file: Blob) {
    const {
      actionStore: api, waitQueueStore, modalStore, publicStore,
    } = this.props;

    const tempInfo = await api.tempUpload(file, filename);

    waitQueueStore.append('호기일괄등록', 'LAZYTASK-wb_excel', async (response) => {
      ConfirmWarning.show(response.titlebox, response.messagebox);
      await this.onRetrieveEvent();
    }, () => this.unitExcelUpload(filename, file));
    modalStore?.openWaitQueue();

    if (await api.exec(
      Category.GENERAL,
      'wb_e611_excel',
      {
        actcd: this.state.actInfo!.actcd,
        cltcd: this.state.actInfo!.cltcd,
        new: '0',
        custcd: publicStore.user.custcd,
        fileext: filename.lastIndexOf('.') > -1 ? filename.substr(filename.lastIndexOf('.') + 1) : '',
        tempfile: tempInfo.data,
        dir: tempInfo.data.dir,
        filename: tempInfo.data.filename,
        size: tempInfo.data.size,
        src: tempInfo.data.src,
      },
      false,
    )) {
      await this.onRetrieveEvent();
    }
  }

  // -------------------------------------------------------------------------
  // 행 업데이트 핸들러
  // -------------------------------------------------------------------------

  @action
  onUpdatedRows(rows: any, updatedRows: any) {
    this.setState({
      [TAB_CONFIG.updatesStateNames[this.tabIndex]]: updatedRows,
      [TAB_CONFIG.dataStateNames[this.tabIndex]]: rows,
    } as any);
  }

  @action
  onUpdatedRows2(rows2: any, updatedRows2: any) {
    this.updatedRows2 = updatedRows2;
    this.setState({ PersonPopUpData: rows2 });
  }

  @action
  onUpdatedRows3(rows3: any, updatedRows3: any) {
    this.updatedRows3 = updatedRows3;
    this.setState({ historyData: rows3 });
  }

  @action
  onUpdatedRows4(rows4: any, updatedRows4: any) {
    this.updatedRows4 = updatedRows4;
    this.setState({ contractLongData: rows4 });
  }

  @action
  onUpdatedRows5(rows5: any, updatedRows5: any) {
    this.updatedRows5 = updatedRows5;
    this.setState({ changePopUpData: rows5 });
  }

  // -------------------------------------------------------------------------
  // 렌더
  // -------------------------------------------------------------------------

  render() {
    return (
      <EnrollmentTemplate
        scope={this}
        update={(change, callback) => {
          this.setState({ ...this.state, ...change }, () => callback?.());
        }}
        dataUpdate={(change, callback) => {
          this.setState({
            actInfo: { ...this.state.actInfo, ...change },
            inchargesFocused: {
              ...(this.state as any)[TAB_CONFIG.dataStateNames[this.tabIndex]],
              ...change,
            },
          } as any, () => callback?.());
        }}
      />
    );
  }
}
